/* eslint-disable */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { dossiersAPI, draftAPI, teamAPI } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCatalog } from '../hooks/useCatalog';
import CatalogPill, { CatalogPillsStack } from '../components/CatalogPill';
import InlineCatalogPicker from '../components/InlineCatalogPicker';
import FormalistePicker from '../components/FormalistePicker';
import DossierWorkspace from '../components/DossierWorkspace';
import SendDocumentsModal from '../components/SendDocumentsModal';

const API_URL = process.env.REACT_APP_API_URL || 'https://lexback-production.up.railway.app';

// Valeur copiable en un clic (liasse, SIREN…) — usage dans la liste.
function CopyMini({ text }) {
  const [copied, setCopied] = useState(false);
  if (text == null || text === '') return null;
  return (
    <span
      onClick={e => { e.stopPropagation(); try { navigator.clipboard.writeText(String(text)); } catch (_) {} setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      title="Cliquer pour copier"
      style={{ cursor:'pointer', fontFamily:'monospace', color:'#5C6B7A', display:'inline-flex', alignItems:'center', gap:'4px' }}
    >
      {text}
      <span style={{ fontSize:'9px', color: copied ? '#1A7A4A' : '#c7bfa9' }}>{copied ? '✓' : '⧉'}</span>
    </span>
  );
}
const CAPACITE_PAR_FORMALISTE = 12;

// Couleurs fallback pour les statuts internes (utilisées si pas de status_label_id)
const STATUS_COLORS = {
  recu:'#6366f1', assigne:'#8b5cf6', en_cours:'#B8860B', depose:'#3b82f6',
  termine:'#22c55e', rejete:'#dc2626', doc_manquant:'#f59e0b',
};
const STATUS_LABELS = {
  recu:'Reçu', assigne:'Assigné', en_cours:'En cours', depose:'Déposé',
  termine:'Terminé', rejete:'Rejeté', doc_manquant:'Doc manquant',
};

// ── Grille partagée de la liste dossiers (liste dense) ──
//   case | Société | Formalité | Statut | Assigné | Date | Docs | Actions
//   Modifier ici la garde l'en-tête, la ligne d'ajout et les lignes alignés.
const DOSSIER_GRID = '30px 92px minmax(190px, 1fr) 150px 70px 280px 116px';

function initialsFrom(nom) {
  if (!nom) return null;
  const p = nom.trim().split(/\s+/);
  const ini = ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase();
  return ini || null;
}

function AvatarCircle({ initials, size = 26 }) {
  if (!initials) {
    return (
      <span style={{
        width: size, height: size, borderRadius: '50%',
        border: '1px dashed rgba(0,0,0,0.18)', display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', color: '#b8b2a4',
        fontSize: '13px', lineHeight: 1,
      }}>+</span>
    );
  }
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%',
      background: 'rgba(91,141,239,0.12)', color: '#3b6ea5',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size <= 22 ? '9px' : '10px', fontWeight: 600, lineHeight: 1,
    }}>{initials}</span>
  );
}

// Cellule "Assigné" : avatar cliquable → menu des collaborateurs du workspace.
// Lecture seule si !canEdit. S'appuie sur la liste `formalistes` déjà chargée
// dans la page (teamAPI.formalistes) et sur onAssign(dossierId, formalisteId|null).
function AssigneCell({ dossier, formalistes, canEdit, onAssign }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const current = formalistes.find(f => f.id === dossier.formaliste_id);
  const initials = current
    ? (current.initiales || initialsFrom(`${current.prenom} ${current.nom}`))
    : initialsFrom(dossier.formaliste_nom);

  if (!canEdit) return <AvatarCircle initials={initials} />;

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }} onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(o => !o)}
        title={current ? `${current.prenom} ${current.nom}` : (dossier.formaliste_nom || 'Assigner')}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, display: 'inline-flex' }}
      >
        <AvatarCircle initials={initials} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '30px', left: 0, zIndex: 40, minWidth: '190px',
          background: 'white', border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '4px', maxHeight: '260px', overflowY: 'auto',
        }}>
          {dossier.formaliste_id && (
            <button onClick={() => { onAssign(dossier.id, null); setOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '7px 10px', borderRadius: '6px', fontSize: '12.5px', color: '#B03020', cursor: 'pointer', fontFamily: 'inherit' }}>
              Désassigner
            </button>
          )}
          {formalistes.length === 0 && (
            <div style={{ padding: '8px 10px', fontSize: '11.5px', color: '#9aa3b0' }}>Aucun collaborateur</div>
          )}
          {formalistes.map(f => (
            <button key={f.id} onClick={() => { onAssign(dossier.id, f.id); setOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', width: '100%', textAlign: 'left', background: f.id === dossier.formaliste_id ? 'rgba(184,134,11,0.06)' : 'transparent', border: 'none', padding: '6px 8px', borderRadius: '6px', fontSize: '12.5px', color: '#1d1d1f', cursor: 'pointer', fontFamily: 'inherit', fontWeight: f.id === dossier.formaliste_id ? 600 : 400 }}>
              <span style={{ marginRight: '8px', flexShrink: 0 }}>
                <AvatarCircle initials={f.initiales || initialsFrom(`${f.prenom} ${f.nom}`)} size={20} />
              </span>
              {f.prenom} {f.nom}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

// ── Types principaux pour la création inline (style Airtable) ──
// Aligné avec NouveauDossier.js : ces clés sont les `type_formalite` attendues
// par l'endpoint /api/dossiers/draft + /submit côté backend.
const INLINE_MAIN_TYPES = [
  { id: 'creation',    label: 'Création de société' },
  { id: 'modification',label: 'Modification statutaire' },
  { id: 'fermeture',   label: 'Fermeture / Dissolution' },
  { id: 'depot_acte',  label: 'Dépôt d\'acte' },
];

// ── Format propre des erreurs renvoyées par l'API ──
// FastAPI renvoie selon les cas :
// - string :          { detail: "Message" }
// - HTTPException :   { detail: "Texte" }
// - validation 422 :  { detail: [{loc, msg, type, ...}, ...] }   ← celui qui produisait "[object Object]"
// - objet maison :    { detail: {...} }
// On retourne TOUJOURS une string lisible.
function formatApiError(err, fallback = 'Une erreur est survenue') {
  // Log complet en console pour le debug
  try { console.error('[API error]', err?.response?.status, err?.response?.data, err); } catch {}
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  const d = data.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    // Erreurs de validation Pydantic
    const msgs = d.map(it => {
      if (typeof it === 'string') return it;
      const loc = Array.isArray(it?.loc) ? it.loc.filter(p => p !== 'body').join('.') : '';
      const msg = it?.msg || it?.message || JSON.stringify(it);
      return loc ? `${loc}: ${msg}` : msg;
    });
    return msgs.join(' • ');
  }
  if (d && typeof d === 'object') {
    if (typeof d.message === 'string') return d.message;
    try { return JSON.stringify(d); } catch { return fallback; }
  }
  if (typeof data === 'string') return data;
  if (data.message) return data.message;
  return fallback;
}

// ── Détecte si un dossier est "verrouillé" pour le client ──
// Le client perd le droit de modifier le statut/commentaire dès que :
// - le statut interne est "termine" (Kbis reçu, Validé)
// - le label du statut contient "kbis", "terminé", "validé", "clos", "annulé"
function isDossierLocked(dossier) {
  const statusName = (dossier?.status_label?.name || '').toLowerCase();
  const status = (dossier?.status || '').toLowerCase();
  if (status === 'termine') return true;
  if (
    statusName.includes('kbis') ||
    statusName.includes('terminé') ||
    statusName.includes('validé') ||
    statusName.includes('valide') ||
    statusName.includes('clos') ||
    statusName.includes('clôtu') ||
    statusName.includes('annul')
  ) return true;
  return false;
}

// ── Classification relais ── 
// Détermine où en est le dossier dans la chaîne :
// - lexback : chez Lexback (en cours de traitement par le formaliste)
// - client : chez le client (attente signature, doc manquant côté client)
// - greffe : chez le greffe (transmis, en cours INPI)
// - clos : dossier clos (Kbis reçu, terminé)
function getDossierRelay(dossier) {
  const statusName = (dossier.status_label?.name || '').toLowerCase();
  const status = (dossier.status || '').toLowerCase();

  // ANNULÉ — uniquement les statuts qui contiennent littéralement "annulé"
  if (statusName.includes('annul')) {
    return 'annule';
  }
  // CLOS — Kbis reçu / Terminé
  if (statusName.includes('kbis') || statusName.includes('terminé') || statusName.includes('clôtu') || statusName.includes('clos') || status === 'termine') {
    return 'clos';
  }
  // CHEZ LE GREFFE — transmis, déposé, en cours INPI
  if (statusName.includes('transmis') || statusName.includes('greffe') || statusName.includes('gtc') || statusName.includes('inpi') || statusName.includes('déposé') || statusName.includes('depose') || statusName.includes('insee') || statusName.includes('dreal') || statusName.includes('cma') || statusName.includes('sie') || status === 'depose') {
    return 'greffe';
  }
  // CHEZ CLIENT — attente signature, doc manquant
  if (statusName.includes('signature') || statusName.includes('signer') || statusName.includes('manquant') || statusName.includes('attente client') || statusName.includes('relance') || status === 'doc_manquant') {
    return 'client';
  }
  // CHEZ LEXBACK — tout le reste (reçu, en cours formaliste, à traiter, rédaction...)
  return 'lexback';
}

// Classification pill couleur selon nom de statut (sans emoji)
// Renvoie { bg, fg } pour la pill ovale
// ── Couleur de la pill "Formalité" ──
// Si le catalogue fournit une couleur, on l'utilise. Sinon fallback déterministe
// par mot-clé du nom de la formalité, pour qu'aucune pill ne soit gris terne.
function getFormalityPillColor(label) {
  const name = (label?.name || '').toLowerCase();
  // Priorité : couleur explicite du catalogue
  if (label?.color) {
    return { bg: `${label.color}1A`, fg: label.color };
  }
  // Fallback par famille
  if (name.includes('création') || name.includes('creation')) {
    return { bg: '#dbeafe', fg: '#1e40af' }; // bleu — création
  }
  if (name.includes('modif') || name.includes('cession') || name.includes('changement') || name.includes('transfert') || name.includes('nomination')) {
    return { bg: '#ede9fe', fg: '#6b21a8' }; // violet — modification
  }
  if (name.includes('dissolution') || name.includes('fermeture') || name.includes('clôtu') || name.includes('clotu') || name.includes('liquidation') || name.includes('radiation')) {
    return { bg: '#fee2e2', fg: '#b91c1c' }; // rouge — fermeture
  }
  if (name.includes('dépôt') || name.includes('depot') || name.includes('compte') || name.includes('acte')) {
    return { bg: '#fef3c7', fg: '#92400e' }; // ambre — dépôt
  }
  // Default visible (pas gris terne)
  return { bg: '#e0f2fe', fg: '#0369a1' }; // bleu ciel
}

function getStatusPillColor(dossier) {
  const statusName = (dossier.status_label?.name || '').toLowerCase();
  const status = (dossier.status || '').toLowerCase();

  // Gris foncé — Annulé (en premier pour la priorité)
  if (statusName.includes('annul')) {
    return { bg: '#e5e7eb', fg: '#374151', strikethrough: true };
  }
  // Vert — Kbis / Terminé / Validé
  if (statusName.includes('kbis') || statusName.includes('terminé') || statusName.includes('validé') || statusName.includes('clos') || status === 'termine') {
    return { bg: '#d1fae5', fg: '#047857' };
  }
  // Bleu — Transmis / Déposé / En cours INPI (mouvement avec le greffe)
  if (statusName.includes('transmis') || statusName.includes('déposé') || statusName.includes('depose') || statusName.includes('insee') || statusName.includes('dreal') || statusName.includes('cma') || statusName.includes('sie') || statusName.includes('inpi') || status === 'depose') {
    return { bg: '#dbeafe', fg: '#1e40af' };
  }
  // Rouge — Rejeté / Document manquant / Bloqué
  if (statusName.includes('rejet') || statusName.includes('manquant') || statusName.includes('bloqu') || statusName.includes('erreur') || status === 'rejete' || status === 'doc_manquant') {
    return { bg: '#fee2e2', fg: '#b91c1c' };
  }
  // Ambre — Attente signature / À signer / À traiter
  if (statusName.includes('signature') || statusName.includes('signer') || statusName.includes('attente') || statusName.includes('à traiter') || statusName.includes('a traiter') || statusName.includes('relance')) {
    return { bg: '#fef3c7', fg: '#92400e' };
  }
  // Doré — En cours / Préparation
  if (statusName.includes('cours') || statusName.includes('rédiger') || statusName.includes('redaction') || statusName.includes('préparation') || statusName.includes('preparation') || status === 'en_cours') {
    return { bg: '#fef3c7', fg: '#a07d2c' };
  }
  // Gris clair — Reçu / Autre par défaut
  return { bg: '#f3f4f6', fg: '#4b5563' };
}

// Sources : API, email, whatsapp, slack, airtable, plateforme (manuel), webhook
const SOURCE_META = {
  api:       { label: 'API',       bg: '#1a1a1a', fg: '#B8860B', text: '</>' },
  email:     { label: 'Email',     bg: '#dbeafe', fg: '#1d4ed8', text: '✉' },
  whatsapp:  { label: 'WhatsApp',  bg: '#dcfce7', fg: '#16a34a', text: 'W' },
  slack:     { label: 'Slack',     bg: '#ede9fe', fg: '#6b21a8', text: '#' },
  airtable:  { label: 'Airtable',  bg: '#fde68a', fg: '#b45309', text: 'AT' },
  webhook:   { label: 'Webhook',   bg: '#fef3c7', fg: '#b45309', text: '⚡' },
  plateforme:{ label: 'Manuel',    bg: '#f3f4f6', fg: '#6b7280', text: '↑' },
};

function SourceIcon({ source }) {
  const meta = SOURCE_META[source] || SOURCE_META.plateforme;
  return (
    <div title={`Source : ${meta.label}`} style={{
      width:'18px', height:'18px', background: meta.bg, borderRadius:'4px',
      display:'inline-flex', alignItems:'center', justifyContent:'center',
      color: meta.fg, fontSize:'9px', fontWeight:700, flexShrink:0,
      fontFamily: source === 'api' ? 'JetBrains Mono, monospace' : 'inherit',
    }}>{meta.text}</div>
  );
}

// Calcul coûts INPI + JAL — tarifs réels 2026 (arrêté 25 février 2026, JORF 28/02/2026)
function getDossierCosts(d) {
  if (!d) return { total: 0, inpi: 0, jal: 0, detail: '' };
  const t = (d.type || '').toLowerCase();
  const subs = (d.sub_type_labels || []).map(s => (s.code || s.name || '').toLowerCase());

  let inpi = 0, jal = 0;
  let parts = [];

  // INPI / greffe
  if (t.includes('creation') || t === 'creation_sas' || t === 'creation_sarl' || t === 'creation_sasu' || t === 'creation_eurl') {
    inpi = 35.59; parts.push('35,59 € greffe');
    // RBE inclus dès qu'il y a une création
    inpi += 20.34; parts.push('20,34 € RBE');
  } else if (t === 'creation_sci') {
    inpi = 66.88; parts.push('66,88 € greffe SCI');
    inpi += 20.34; parts.push('20,34 € RBE');
  } else if (t === 'modification' || t.startsWith('modif')) {
    // Modif avec Bodacc par défaut
    inpi = 192.01; parts.push('192,01 € greffe');
  } else if (t === 'dissolution') {
    inpi = 177.01; parts.push('177,01 € dissolution');
  } else if (t === 'radiation') {
    inpi = 9.19; parts.push('9,19 € radiation');
  } else if (t === 'transfert_siege') {
    inpi = 75; parts.push('75 € transfert');
  } else if (t === 'depot_comptes' || t === 'depot_acte') {
    inpi = 55; parts.push('55 € dépôt');
  }

  // JAL : seulement pour les formalités qui en requièrent (création + modification + dissolution + transfert)
  if (t.includes('creation') || t === 'modification' || t.startsWith('modif') || t === 'dissolution' || t === 'transfert_siege') {
    // Fourchette moyenne (sera affinée selon département en prod)
    if (t.includes('creation')) jal = 165;
    else if (t === 'dissolution') jal = 175;
    else if (t === 'transfert_siege') jal = 145;
    else jal = 155;
    parts.push(`${jal.toFixed(0)} € JAL`);
  }

  const total = inpi + jal;
  return { total, inpi, jal, detail: parts.join(' + ') };
}

function CostBadge({ dossier }) {
  const { total, detail } = getDossierCosts(dossier);
  if (total === 0) {
    return (
      <div style={{ textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:'11.5px' }}>
        <div style={{ color:'var(--color-text-primary)', fontWeight:500 }}>— €</div>
        <div style={{ fontSize:'10px', color:'var(--color-text-tertiary)' }}>À provisionner</div>
      </div>
    );
  }
  return (
    <div style={{ textAlign:'right', fontVariantNumeric:'tabular-nums', fontSize:'11.5px' }}>
      <div style={{ color:'var(--color-text-primary)', fontWeight:500 }}>{total.toFixed(2).replace('.', ',')} €</div>
      <div style={{ fontSize:'10px', color:'var(--color-text-tertiary)' }} title={detail}>{detail.length > 30 ? detail.substring(0, 28) + '…' : detail}</div>
    </div>
  );
}

// ── Document categories : 6 onglets fixes pour le drawer ──
const DOC_TABS = ['Statuts', 'PV', 'Kbis', 'RBE', 'Cession', 'Autres'];

// Détermine si une subcategory correspond à une des 5 catégories métier.
// Ex: "Statuts AZAD" → "Statuts" ; "PV AGE" → "PV" ; "RBE déclaration" → "RBE"
// Tout ce qui n'est classifié dans aucune catégorie tombe automatiquement dans "Autres".
function classifyAttachment(subcategory) {
  if (!subcategory) return 'Autres';
  const s = subcategory.toLowerCase();
  if (s.includes('statut')) return 'Statuts';
  if (s.includes('pv') || s.includes('proc') || s.includes('procès-verbal') || s.includes('proces-verbal') || s.includes('age') || s.includes('agoa') || s.includes('agoe')) return 'PV';
  if (s.includes('kbis') || s.includes('extrait')) return 'Kbis';
  if (s.includes('rbe') || s.includes('bénéf') || s.includes('benef') || s.includes('beneficiaire') || s.includes('bénéficiaire')) return 'RBE';
  if (s.includes('cession') || s.includes('cédant') || s.includes('cedant') || s.includes('cessionnaire') || s.includes('vente') || s.includes('parts')) return 'Cession';
  return 'Autres';
}

// Aplatit la réponse API attachments en un seul tableau, en filtrant lexback_internal pour les clients
function flattenAttachments(attachmentsByCategory, role) {
  if (!attachmentsByCategory || typeof attachmentsByCategory !== 'object') return [];
  const flat = [];
  Object.entries(attachmentsByCategory).forEach(([cat, items]) => {
    if (!Array.isArray(items)) return;
    if (role === 'client' && cat === 'lexback_internal') return;
    items.forEach(it => flat.push({ ...it, _topCategory: cat }));
  });
  return flat;
}

// Retourne un objet { Statuts: [docs], PV: [...], Kbis: [...], RBE: [...], Cession: [...], Autres: [...] }
function groupAttachmentsByTab(attachmentsByCategory, role) {
  const buckets = { Statuts: [], PV: [], Kbis: [], RBE: [], Cession: [], Autres: [] };
  flattenAttachments(attachmentsByCategory, role).forEach(att => {
    const tab = classifyAttachment(att.subcategory);
    buckets[tab].push(att);
  });
  return buckets;
}

// Petit composant qui affiche un dot vert (✓) si au moins un doc, gris sinon
function DocCellIndicator({ count }) {
  if (count > 0) {
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        color: '#10B981',
        fontSize: '11px',
        fontWeight: 600,
      }}>
        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10B981' }} />
        {count > 1 ? count : '✓'}
      </span>
    );
  }
  return <span style={{ color: 'rgba(0,0,0,0.18)', fontSize: '14px' }}>—</span>;
}

function getInitials(nom) {
  if (!nom) return '?';
  return nom.trim().split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function formatDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric' }) +
    ' ' + dt.toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' });
}

function FrmAvatar({ nom, count }) {
  const pct = count != null ? count / CAPACITE_PAR_FORMALISTE : 0;
  const ringColor = pct >= 1 ? '#dc2626' : pct >= 0.75 ? '#f59e0b' : '#22c55e';
  // Couleur stable dérivée du nom
  const palette = ['#B8860B', '#8b5cf6', '#3b82f6', '#16a34a', '#dc2626', '#f59e0b', '#0ea5e9', '#a855f7'];
  const hash = (nom || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const bg = palette[hash % palette.length];
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
      <div style={{
        width:'26px', height:'26px', borderRadius:'50%',
        background: bg, display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:'10px', fontWeight:600, color:'white',
        boxShadow: count != null ? `0 0 0 1.5px ${ringColor}, 0 0 0 2.5px var(--color-background-primary)` : 'none',
        flexShrink:0,
      }}>
        {getInitials(nom)}
      </div>
      <span style={{ fontSize:'12.5px', color:'var(--color-text-primary)', whiteSpace:'nowrap' }}>{nom}</span>
    </div>
  );
}

function StatusFallbackBadge({ status }) {
  const color = STATUS_COLORS[status] || '#888';
  return (
    <span style={{ fontSize:'10px', fontWeight:500, padding:'2px 8px', borderRadius:'4px', color, background:`${color}18` }}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════
// DISCUSSION — fil de messages collaboratif équipe + client
// Visibility: 'internal' (équipe seulement) ou 'shared' (visible client)
// + @mentions, réactions emoji, édition/suppression
// ═══════════════════════════════════════════════════════════════

const ALLOWED_REACTIONS = ['👍', '✅', '👀', '❤️', '🎉', '👎'];

function _avatarColorFromName(name) {
  const palette = ['#B8860B', '#8b5cf6', '#3b82f6', '#16a34a', '#dc2626', '#f59e0b', '#0ea5e9', '#a855f7'];
  const hash = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return palette[hash % palette.length];
}

function _initialsOf(name) {
  if (!name) return '?';
  return name.trim().split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function _formatRelativeDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `il y a ${Math.floor(diff / 86400)} j`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

// Rendu d'un contenu avec les @mentions en pills colorées
function _renderContentWithMentions(content, mentionsById) {
  if (!content) return null;
  const regex = /@([A-Za-zÀ-ÿ\u00C0-\u017F][A-Za-zÀ-ÿ\u00C0-\u017F'\- ]{0,40}?)(?=[.,!?;:\s]|$)/g;
  const out = [];
  let last = 0;
  let m;
  let key = 0;
  while ((m = regex.exec(content)) !== null) {
    if (m.index > last) out.push(<span key={key++}>{content.slice(last, m.index)}</span>);
    const name = m[1].trim();
    // Match avec un user mentionné connu (par nom)
    const matched = mentionsById && Object.values(mentionsById).find(u =>
      u.name && u.name.toLowerCase() === name.toLowerCase()
    );
    if (matched) {
      out.push(
        <span key={key++} style={{
          display: 'inline-block',
          background: 'rgba(184,134,11,0.12)',
          color: '#B8860B',
          padding: '0 5px',
          borderRadius: '4px',
          fontWeight: 600,
          fontSize: '12px',
        }}>@{matched.name}</span>
      );
    } else {
      out.push(<span key={key++}>{m[0]}</span>);
    }
    last = m.index + m[0].length;
  }
  if (last < content.length) out.push(<span key={key++}>{content.slice(last)}</span>);
  return out;
}

function DiscussionSection({ dossierId, currentUserId, currentUserRole, canEdit, forcedVisibility, title }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  // Visibility par défaut : forcée si prop, sinon interne pour les internes / shared pour les clients
  const [visibility, setVisibility] = useState(forcedVisibility || (canEdit ? 'internal' : 'shared'));
  // Mentions
  const [mentionables, setMentionables] = useState([]); // [{id, name, email, role}]
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [emojiPickerFor, setEmojiPickerFor] = useState(null); // note_id ou null
  const textareaRef = React.useRef(null);

  const isInternalUser = canEdit; // canEdit = admin/formaliste/superviseur

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('lexback_token')}` });

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/notes/${dossierId}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        let arr = Array.isArray(data) ? data : [];
        // Si forcedVisibility, filtrer côté frontend pour n'afficher QUE ces messages
        if (forcedVisibility) {
          arr = arr.filter(m => m.visibility === forcedVisibility);
        }
        setMessages(arr);
      } else {
        setMessages([]);
      }
    } catch { setMessages([]); }
    finally { setLoading(false); }
  }, [dossierId, forcedVisibility]);

  // Charger les utilisateurs mentionnables (interne uniquement)
  useEffect(() => {
    if (!isInternalUser) return;
    fetch(`${API_URL}/api/team/mentionable/${dossierId}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setMentionables(Array.isArray(data) ? data : []))
      .catch(() => setMentionables([]));
  }, [dossierId, isInternalUser]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Map id → user pour le rendu des mentions
  const mentionablesById = React.useMemo(() => {
    const m = {};
    mentionables.forEach(u => { m[u.id] = u; });
    return m;
  }, [mentionables]);

  // Détection du @ dans le composer pour ouvrir l'autocomplete
  const handleDraftChange = (e) => {
    const value = e.target.value;
    setDraft(value);
    const pos = e.target.selectionStart;
    const before = value.slice(0, pos);
    const match = before.match(/(?:^|\s)@([A-Za-zÀ-ÿ\u00C0-\u017F][A-Za-zÀ-ÿ\u00C0-\u017F'\- ]{0,30})$/);
    if (match && isInternalUser) {
      setMentionOpen(true);
      setMentionQuery(match[1]);
      setMentionIndex(0);
    } else {
      setMentionOpen(false);
    }
  };

  const filteredMentionables = React.useMemo(() => {
    if (!mentionQuery) return mentionables.slice(0, 6);
    const q = mentionQuery.toLowerCase();
    return mentionables.filter(u =>
      u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    ).slice(0, 6);
  }, [mentionables, mentionQuery]);

  const insertMention = (user) => {
    if (!user) return;
    const textarea = textareaRef.current;
    if (!textarea) return;
    const pos = textarea.selectionStart;
    const before = draft.slice(0, pos);
    const after = draft.slice(pos);
    // Remplacer @<query> par @<name> + espace
    const newBefore = before.replace(/(?:^|\s)@([A-Za-zÀ-ÿ\u00C0-\u017F][A-Za-zÀ-ÿ\u00C0-\u017F'\- ]{0,30})$/, (m, p1, off) => {
      const prefix = off === 0 ? '' : m[0]; // garder l'espace devant
      return `${prefix === ' ' || prefix === '\n' ? prefix : ''}@${user.name} `;
    });
    setDraft(newBefore + after);
    setMentionOpen(false);
    requestAnimationFrame(() => {
      textarea.focus();
      const newPos = newBefore.length;
      textarea.setSelectionRange(newPos, newPos);
    });
  };

  // Détecter les @noms dans le draft pour construire mentioned_user_ids
  const extractMentionedIds = (text) => {
    if (!text || !isInternalUser) return [];
    const ids = new Set();
    mentionables.forEach(u => {
      const regex = new RegExp(`(?:^|\\s)@${u.name.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}(?=[.,!?;:\\s]|$)`, 'i');
      if (regex.test(text)) ids.add(u.id);
    });
    return Array.from(ids);
  };

  const handleSend = async () => {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const payload = {
        content,
        visibility: isInternalUser ? visibility : 'shared',
        mentioned_user_ids: extractMentionedIds(content),
      };
      const res = await fetch(`${API_URL}/api/notes/${dossierId}`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur envoi');
      }
      const note = await res.json();
      setMessages(prev => [...prev, note]);
      setDraft('');
      setMentionOpen(false);
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    // Navigation dans le dropdown mention
    if (mentionOpen && filteredMentionables.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(i => Math.min(i + 1, filteredMentionables.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMentionables[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionOpen(false);
        return;
      }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/notes/note/${noteId}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Erreur suppression');
      setMessages(prev => prev.filter(n => n.id !== noteId));
    } catch (e) {
      alert(e.message);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditDraft(note.content);
  };
  const cancelEdit = () => { setEditingId(null); setEditDraft(''); };
  const saveEdit = async (noteId) => {
    const content = editDraft.trim();
    if (!content) return;
    try {
      const res = await fetch(`${API_URL}/api/notes/note/${noteId}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) throw new Error('Erreur édition');
      const updated = await res.json();
      setMessages(prev => prev.map(n => n.id === noteId ? updated : n));
      cancelEdit();
    } catch (e) {
      alert(e.message);
    }
  };

  const toggleReaction = async (noteId, emoji) => {
    try {
      const res = await fetch(`${API_URL}/api/notes/note/${noteId}/reaction`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji }),
      });
      if (!res.ok) throw new Error('Erreur réaction');
      const updated = await res.json();
      setMessages(prev => prev.map(n => n.id === noteId ? updated : n));
    } catch (e) {
      alert(e.message);
    }
    setEmojiPickerFor(null);
  };

  // Titre + icône selon visibility forcée
  const sectionIcon = forcedVisibility === 'internal' ? '🔒' : '💬';
  const sectionTitle = title || (forcedVisibility === 'internal' ? 'Notes internes Lexback'
                                : forcedVisibility === 'shared' ? 'Discussion avec le client'
                                : 'Discussion');

  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <div style={{ fontWeight: 600, fontSize: '13px', color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>{sectionIcon}</span>
          <span>{sectionTitle}</span>
          {messages.length > 0 && (
            <span style={{ fontSize: '11px', fontWeight: 500, color: '#9a8f7a', background: '#f5f1ea', padding: '1px 7px', borderRadius: '10px' }}>{messages.length}</span>
          )}
        </div>
      </div>

      {/* Liste des messages */}
      {loading ? (
        <div style={{ fontSize: '12px', color: '#9a8f7a', textAlign: 'center', padding: '12px' }}>Chargement…</div>
      ) : messages.length === 0 ? (
        <div style={{ fontSize: '12px', color: '#bfb5a3', textAlign: 'center', padding: '16px', fontStyle: 'italic', background: '#fdfbf5', border: '1px dashed #e5e1d8', borderRadius: '8px', marginBottom: '10px' }}>
          Aucun message pour l'instant — démarrez la conversation
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          {messages.map(msg => {
            const isOwn = msg.author_id === currentUserId;
            const isAdmin = currentUserRole === 'admin';
            const canEditMsg = isOwn || isAdmin;
            const isEditing = editingId === msg.id;
            const isInternal = msg.visibility === 'internal';
            // Récupérer une couleur de bordure légère selon visibility
            const borderColor = isInternal ? '#f3d77a' : '#9fd9b3';
            const bgColor = isInternal ? '#fdfaef' : '#f3faf6';
            const visibilityBadge = isInternal
              ? { label: '🔒 Interne', bg: '#fef3c7', fg: '#92400e' }
              : { label: '🌐 Visible client', bg: '#dcfce7', fg: '#16653a' };

            return (
              <div key={msg.id} style={{
                display: 'flex', gap: '10px',
                padding: '10px 12px',
                background: bgColor,
                border: `1px solid ${borderColor}`,
                borderRadius: '10px',
                position: 'relative',
              }}>
                {/* Avatar */}
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: _avatarColorFromName(msg.author_name),
                  color: 'white', fontSize: '10px', fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>{_initialsOf(msg.author_name)}</div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#1a1a1a' }}>{msg.author_name || 'Inconnu'}</span>
                      {isOwn && <span style={{ fontSize: '10px', fontWeight: 400, color: '#9a8f7a' }}>(vous)</span>}
                      {/* Badge visibility — visible uniquement par les internes */}
                      {isInternalUser && (
                        <span style={{ fontSize: '9.5px', fontWeight: 600, padding: '1px 6px', borderRadius: '3px', background: visibilityBadge.bg, color: visibilityBadge.fg }}>
                          {visibilityBadge.label}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '10.5px', color: '#9a8f7a', whiteSpace: 'nowrap' }}>
                      {_formatRelativeDate(msg.created_at)}
                      {msg.updated_at && <span title={`Modifié le ${new Date(msg.updated_at).toLocaleString('fr-FR')}`}> · édité</span>}
                    </div>
                  </div>

                  {isEditing ? (
                    <>
                      <textarea
                        value={editDraft}
                        onChange={e => setEditDraft(e.target.value)}
                        rows={3}
                        style={{ width: '100%', border: '1px solid #e5e1d8', borderRadius: '6px', padding: '6px 8px', fontSize: '13px', fontFamily: 'inherit', background: '#fff', color: '#1a1a1a', resize: 'vertical' }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '4px' }}>
                        <button onClick={cancelEdit} style={{ background: 'transparent', border: '1px solid #e5e1d8', padding: '3px 10px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer', color: '#9a8f7a' }}>Annuler</button>
                        <button onClick={() => saveEdit(msg.id)} disabled={!editDraft.trim()} style={{ background: '#B8860B', color: '#fff', border: 'none', padding: '3px 10px', borderRadius: '5px', fontSize: '11px', cursor: 'pointer' }}>Enregistrer</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '13px', color: '#1a1a1a', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.45 }}>
                        {_renderContentWithMentions(msg.content, mentionablesById)}
                      </div>

                      {/* Réactions */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px', flexWrap: 'wrap' }}>
                        {Object.entries(msg.reactions || {}).map(([emoji, userIds]) => {
                          const hasMine = userIds.includes(currentUserId);
                          return (
                            <button
                              key={emoji}
                              onClick={() => toggleReaction(msg.id, emoji)}
                              title={userIds.length === 1 ? '1 personne' : `${userIds.length} personnes`}
                              style={{
                                background: hasMine ? 'rgba(184,134,11,0.15)' : '#fff',
                                border: hasMine ? '1px solid #B8860B' : '1px solid #e5e1d8',
                                borderRadius: '12px',
                                padding: '1px 7px',
                                fontSize: '12px', cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: '3px',
                              }}
                            >
                              <span>{emoji}</span>
                              <span style={{ fontSize: '10px', color: '#5c5446', fontWeight: 500 }}>{userIds.length}</span>
                            </button>
                          );
                        })}

                        {/* Bouton ajouter réaction */}
                        <div style={{ position: 'relative' }}>
                          <button
                            onClick={() => setEmojiPickerFor(emojiPickerFor === msg.id ? null : msg.id)}
                            title="Ajouter une réaction"
                            style={{ background: 'transparent', border: '1px dashed #d4cebd', borderRadius: '12px', padding: '0 8px', fontSize: '11px', cursor: 'pointer', color: '#9a8f7a', height: '22px' }}
                          >
                            ＋
                          </button>
                          {emojiPickerFor === msg.id && (
                            <div style={{
                              position: 'absolute', top: '100%', left: 0, marginTop: '4px',
                              background: '#fff', border: '1px solid #e5e1d8', borderRadius: '8px',
                              padding: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              display: 'flex', gap: '2px', zIndex: 10,
                            }}>
                              {ALLOWED_REACTIONS.map(e => (
                                <button
                                  key={e}
                                  onClick={() => toggleReaction(msg.id, e)}
                                  style={{ background: 'transparent', border: 'none', padding: '4px 6px', fontSize: '16px', cursor: 'pointer', borderRadius: '4px' }}
                                  onMouseEnter={ev => { ev.currentTarget.style.background = '#f5f1ea'; }}
                                  onMouseLeave={ev => { ev.currentTarget.style.background = 'transparent'; }}
                                >{e}</button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {canEditMsg && (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                          {isOwn && (
                            <button onClick={() => startEdit(msg)} style={{ background: 'transparent', border: 'none', color: '#9a8f7a', fontSize: '10.5px', cursor: 'pointer', padding: 0 }}>Modifier</button>
                          )}
                          <button onClick={() => handleDelete(msg.id)} style={{ background: 'transparent', border: 'none', color: '#dc2626', fontSize: '10.5px', cursor: 'pointer', padding: 0 }}>Supprimer</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Composer */}
      <div style={{ background: '#fffdf9', border: '1px solid #e5e1d8', borderRadius: '10px', padding: '8px 10px', position: 'relative' }}>
        {/* Toggle visibility — uniquement pour les internes ET si pas de visibility forcée par le parent */}
        {isInternalUser && !forcedVisibility && (
          <div style={{ display: 'flex', gap: '4px', marginBottom: '6px', padding: '2px', background: '#f5f1ea', borderRadius: '6px', width: 'fit-content' }}>
            <button onClick={() => setVisibility('internal')} style={{
              padding: '3px 10px', borderRadius: '4px', fontSize: '11px', border: 'none', cursor: 'pointer',
              background: visibility === 'internal' ? '#fff' : 'transparent',
              color: visibility === 'internal' ? '#92400e' : '#9a8f7a',
              fontWeight: visibility === 'internal' ? 600 : 400,
              boxShadow: visibility === 'internal' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
            }}>🔒 Note interne</button>
            <button onClick={() => setVisibility('shared')} style={{
              padding: '3px 10px', borderRadius: '4px', fontSize: '11px', border: 'none', cursor: 'pointer',
              background: visibility === 'shared' ? '#fff' : 'transparent',
              color: visibility === 'shared' ? '#16653a' : '#9a8f7a',
              fontWeight: visibility === 'shared' ? 600 : 400,
              boxShadow: visibility === 'shared' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
            }}>🌐 Visible client</button>
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={draft}
          onChange={handleDraftChange}
          onKeyDown={handleKeyDown}
          placeholder={
            isInternalUser
              ? (visibility === 'internal'
                ? "Note interne… (tape @ pour mentionner, Cmd+Entrée pour envoyer)"
                : "Message visible par le client… (Cmd+Entrée pour envoyer)")
              : "Écrire un message à l'équipe Lexback… (Cmd+Entrée pour envoyer)"
          }
          rows={2}
          style={{
            width: '100%', border: 'none', outline: 'none', resize: 'vertical',
            fontSize: '13px', fontFamily: 'inherit', background: 'transparent',
            color: '#1a1a1a', minHeight: '48px',
          }}
        />

        {/* Dropdown mentions */}
        {mentionOpen && filteredMentionables.length > 0 && (
          <div style={{
            position: 'absolute', bottom: '100%', left: '10px', marginBottom: '4px',
            background: '#fff', border: '1px solid #e5e1d8', borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10,
            minWidth: '220px', overflow: 'hidden',
          }}>
            <div style={{ fontSize: '10px', color: '#9a8f7a', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '6px 10px 2px', fontWeight: 600 }}>
              Mentionner un collègue
            </div>
            {filteredMentionables.map((u, i) => (
              <div
                key={u.id}
                onClick={() => insertMention(u)}
                onMouseEnter={() => setMentionIndex(i)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '6px 10px', cursor: 'pointer',
                  background: i === mentionIndex ? '#f5f1ea' : 'transparent',
                }}
              >
                <div style={{
                  width: '22px', height: '22px', borderRadius: '50%',
                  background: _avatarColorFromName(u.name),
                  color: 'white', fontSize: '9px', fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{_initialsOf(u.name)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 500, color: '#1a1a1a' }}>{u.name}</div>
                  <div style={{ fontSize: '10px', color: '#9a8f7a' }}>{u.role}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
          <span style={{ fontSize: '10px', color: '#bfb5a3' }}>{draft.length}/10000</span>
          <button
            onClick={handleSend}
            disabled={!draft.trim() || sending}
            style={{
              background: (!draft.trim() || sending) ? '#e5e1d8' : '#B8860B',
              color: (!draft.trim() || sending) ? '#9a8f7a' : '#fff',
              border: 'none', padding: '5px 14px', borderRadius: '6px',
              fontSize: '12px', fontWeight: 500,
              cursor: (!draft.trim() || sending) ? 'not-allowed' : 'pointer',
            }}
          >
            {sending ? 'Envoi…' : 'Publier'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// DOC LIGHTBOX — visionneuse plein écran style WhatsApp pour PDF/images
// docs    : liste plate de documents [{id, nom_original, download_url, mime_type, taille_kb, subcategory}]
// initial : index dans docs (par défaut 0)
// onClose : fermer la lightbox
// ═══════════════════════════════════════════════════════════════
function DocLightbox({ docs, initial = 0, onClose }) {
  const safeDocs = Array.isArray(docs) ? docs.filter(Boolean) : [];
  const [idx, setIdx] = useState(Math.min(Math.max(initial, 0), Math.max(0, safeDocs.length - 1)));

  // ESC pour fermer + flèches gauche/droite pour naviguer + scroll lock body
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowLeft') setIdx(i => Math.max(0, i - 1));
      else if (e.key === 'ArrowRight') setIdx(i => Math.min(safeDocs.length - 1, i + 1));
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [safeDocs.length, onClose]);

  if (safeDocs.length === 0) return null;
  const doc = safeDocs[idx];
  if (!doc) return null;
  const isPdf = (doc.mime_type || '').includes('pdf') || /\.pdf(\?|$)/i.test(doc.nom_original || doc.download_url || '');
  const isImg = (doc.mime_type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(doc.nom_original || doc.download_url || '');

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15, 15, 18, 0.92)',
        display: 'flex', flexDirection: 'column',
        animation: 'lbFadeIn 0.18s ease',
      }}
    >
      {/* Toolbar du haut */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 20px',
        color: '#fff',
        fontFamily: 'DM Sans, sans-serif',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0, flex: 1 }}>
          <span style={{
            background: '#dc2626', color: '#fff',
            width: '32px', height: '32px', borderRadius: '6px',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '11px', fontWeight: 700, flexShrink: 0,
          }}>{isPdf ? 'PDF' : isImg ? 'IMG' : 'DOC'}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {doc.nom_original || `Document ${idx + 1}`}
            </div>
            <div style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.55)', marginTop: '1px' }}>
              {doc.subcategory ? `${doc.subcategory} · ` : ''}
              {doc.taille_kb ? `${Math.round(doc.taille_kb)} Ko` : ''}
              {safeDocs.length > 1 && (
                <span style={{ marginLeft: '10px', color: 'rgba(255,255,255,0.4)' }}>
                  {idx + 1} / {safeDocs.length}
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0, marginLeft: '14px' }}>
          {doc.download_url && (
            <a
              href={doc.download_url}
              download={doc.nom_original || true}
              title="Télécharger"
              style={{
                width: '36px', height: '36px', borderRadius: '7px',
                background: 'rgba(255,255,255,0.10)',
                border: '0.5px solid rgba(255,255,255,0.18)',
                color: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '15px', textDecoration: 'none',
              }}
            >⬇</a>
          )}
          {doc.download_url && (
            <a
              href={doc.download_url}
              target="_blank"
              rel="noopener noreferrer"
              title="Ouvrir dans un nouvel onglet"
              style={{
                width: '36px', height: '36px', borderRadius: '7px',
                background: 'rgba(255,255,255,0.10)',
                border: '0.5px solid rgba(255,255,255,0.18)',
                color: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '14px', textDecoration: 'none',
              }}
            >↗</a>
          )}
          <button
            onClick={onClose}
            title="Fermer (Échap)"
            style={{
              width: '36px', height: '36px', borderRadius: '7px',
              background: 'rgba(255,255,255,0.10)',
              border: '0.5px solid rgba(255,255,255,0.18)',
              color: '#fff',
              cursor: 'pointer', fontSize: '20px', lineHeight: 1,
              fontFamily: 'inherit',
            }}
          >×</button>
        </div>
      </div>

      {/* Zone d'aperçu (avec navigation latérale) */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 20px 20px', minHeight: 0, position: 'relative',
      }}>
        {/* Bouton précédent */}
        {safeDocs.length > 1 && (
          <button
            onClick={() => setIdx(i => Math.max(0, i - 1))}
            disabled={idx === 0}
            title="Précédent (←)"
            style={{
              position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)',
              width: '44px', height: '44px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.10)',
              border: '0.5px solid rgba(255,255,255,0.20)',
              color: '#fff', fontSize: '20px',
              cursor: idx === 0 ? 'not-allowed' : 'pointer',
              opacity: idx === 0 ? 0.3 : 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 2, fontFamily: 'inherit',
            }}
          >‹</button>
        )}

        {/* Contenu */}
        <div style={{
          background: '#fff', borderRadius: '10px',
          width: '100%', maxWidth: '1100px', height: '100%',
          overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {doc.download_url ? (
            isPdf ? (
              <iframe
                src={doc.download_url}
                title={doc.nom_original || 'Document'}
                style={{ width: '100%', height: '100%', border: 'none', background: '#525252' }}
              />
            ) : isImg ? (
              <img
                src={doc.download_url}
                alt={doc.nom_original || ''}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            ) : (
              <div style={{ padding: '40px', color: '#5C6B7A', textAlign: 'center', fontSize: '13px' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📄</div>
                <div>Aperçu non disponible pour ce type de fichier.</div>
                <a
                  href={doc.download_url}
                  download={doc.nom_original || true}
                  style={{
                    display: 'inline-block', marginTop: '12px',
                    padding: '8px 18px', borderRadius: '7px',
                    background: '#B8860B', color: '#fff', textDecoration: 'none',
                    fontSize: '13px', fontWeight: 500,
                  }}
                >Télécharger</a>
              </div>
            )
          ) : (
            <div style={{ padding: '40px', color: '#9a8f7a', fontStyle: 'italic' }}>
              Lien indisponible pour ce document.
            </div>
          )}
        </div>

        {/* Bouton suivant */}
        {safeDocs.length > 1 && (
          <button
            onClick={() => setIdx(i => Math.min(safeDocs.length - 1, i + 1))}
            disabled={idx === safeDocs.length - 1}
            title="Suivant (→)"
            style={{
              position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)',
              width: '44px', height: '44px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.10)',
              border: '0.5px solid rgba(255,255,255,0.20)',
              color: '#fff', fontSize: '20px',
              cursor: idx === safeDocs.length - 1 ? 'not-allowed' : 'pointer',
              opacity: idx === safeDocs.length - 1 ? 0.3 : 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 2, fontFamily: 'inherit',
            }}
          >›</button>
        )}
      </div>

      {/* Animation fade-in */}
      <style>{`
        @keyframes lbFadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DOCS PREVIEW SECTION — 4 onglets fixes Statuts/PV/Kbis/RBE avec iframe PDF
// ═══════════════════════════════════════════════════════════════

function DocsPreviewSection({ dossier, attachments, currentUserRole, onAddDocClick, canAddDoc, canDeleteDoc, onDeleteAtt, isUploadOpen }) {
  const buckets = useMemo(() => groupAttachmentsByTab(attachments, currentUserRole), [attachments, currentUserRole]);

  // Onglet actif : par défaut le premier qui a des docs, sinon Statuts
  const [activeTab, setActiveTab] = useState(() => {
    for (const t of DOC_TABS) if (buckets[t]?.length > 0) return t;
    return 'Statuts';
  });
  // Doc actif dans l'onglet (s'il y en a plusieurs)
  const [activeDocIdx, setActiveDocIdx] = useState(0);

  // Re-sélectionner le 1er doc quand on change d'onglet
  useEffect(() => { setActiveDocIdx(0); }, [activeTab]);

  // Si l'onglet par défaut change (chargement async), bascule sur le premier qui a des docs
  useEffect(() => {
    if (buckets[activeTab]?.length === 0) {
      for (const t of DOC_TABS) if (buckets[t]?.length > 0) { setActiveTab(t); break; }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buckets]);

  const docsInTab = buckets[activeTab] || [];
  const activeDoc = docsInTab[activeDocIdx] || null;

  // ── Lightbox plein écran (style WhatsApp) ──
  // Liste plate de TOUS les docs visibles (toutes catégories) pour permettre
  // la navigation entre eux dans la lightbox.
  const allDocsFlat = useMemo(() => {
    const flat = [];
    for (const tab of DOC_TABS) {
      for (const doc of (buckets[tab] || [])) flat.push(doc);
    }
    return flat;
  }, [buckets]);
  const [lightboxIdx, setLightboxIdx] = useState(null); // null = fermée
  const openLightboxOn = (doc) => {
    if (!doc) return;
    const i = allDocsFlat.findIndex(d => d.id === doc.id);
    setLightboxIdx(i >= 0 ? i : 0);
  };

  return (
    <div style={{ marginBottom: '18px' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '10px',
      }}>
        <div style={{
          fontSize: '11px',
          color: '#8b8576',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 600,
        }}>Documents du dossier</div>
        {canAddDoc && (
          <button
            onClick={onAddDocClick}
            style={{
              background: isUploadOpen ? 'transparent' : 'rgba(91,141,239,0.10)',
              border: isUploadOpen ? '1px solid var(--color-border-tertiary)' : '1px solid rgba(91,141,239,0.30)',
              color: isUploadOpen ? 'var(--color-text-secondary)' : '#5b8def',
              padding: '4px 12px', borderRadius: '6px', fontSize: '11px',
              cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit',
            }}
          >
            {isUploadOpen ? 'Annuler' : '+ Ajouter un document'}
          </button>
        )}
      </div>

      {/* Onglets fixes */}
      <div style={{
        display: 'flex',
        gap: '4px',
        padding: '3px',
        background: '#f3f3f0',
        borderRadius: '8px',
        marginBottom: '12px',
        width: 'fit-content',
      }}>
        {DOC_TABS.map(tab => {
          const count = buckets[tab]?.length || 0;
          const isActive = tab === activeTab;
          const hasDoc = count > 0;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => hasDoc && setActiveTab(tab)}
              disabled={!hasDoc}
              style={{
                padding: '5px 11px',
                borderRadius: '5px',
                background: isActive ? 'white' : 'transparent',
                border: 'none',
                fontSize: '11.5px',
                fontWeight: isActive ? 600 : 500,
                color: !hasDoc ? '#c7c7c5' : (isActive ? '#1d1d1f' : '#8b8576'),
                cursor: hasDoc ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
                boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {tab}
              {count > 0 && (
                <span style={{
                  fontSize: '9.5px',
                  background: isActive ? '#1d1d1f' : 'transparent',
                  color: isActive ? 'white' : '#8b8576',
                  borderRadius: '8px',
                  padding: isActive ? '1px 5px' : 0,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Aperçu du doc actif */}
      {activeDoc ? (
        <div style={{
          background: '#fafaf8',
          border: '0.5px solid rgba(0,0,0,0.08)',
          borderRadius: '10px',
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '9px 14px',
            borderBottom: '0.5px solid rgba(0,0,0,0.06)',
            background: 'white',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
              <span style={{
                background: '#fee',
                color: '#c83737',
                width: '26px',
                height: '26px',
                borderRadius: '5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 700,
                flexShrink: 0,
              }}>PDF</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  onClick={() => openLightboxOn(activeDoc)}
                  title="Ouvrir en plein écran"
                  style={{ fontSize: '12.5px', fontWeight: 500, color: '#1d1d1f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}
                >
                  {activeDoc.nom_original || `${activeTab} ${activeDocIdx + 1}`}
                </div>
                <div style={{ fontSize: '10.5px', color: '#8b8576', fontVariantNumeric: 'tabular-nums' }}>
                  {activeDoc.taille_kb ? `${Math.round(activeDoc.taille_kb)} Ko · ` : ''}{activeDoc.subcategory || ''}
                </div>
              </div>
            </div>
            {/* Switcher si plusieurs docs dans le même onglet */}
            {docsInTab.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px' }}>
                <button
                  type="button"
                  onClick={() => setActiveDocIdx(i => Math.max(0, i - 1))}
                  disabled={activeDocIdx === 0}
                  style={{ width: '24px', height: '24px', borderRadius: '5px', border: '0.5px solid rgba(0,0,0,0.1)', background: 'white', cursor: activeDocIdx === 0 ? 'not-allowed' : 'pointer', opacity: activeDocIdx === 0 ? 0.4 : 1, fontSize: '12px', fontFamily: 'inherit' }}
                  aria-label="Précédent"
                >‹</button>
                <span style={{ fontSize: '10.5px', color: '#8b8576', minWidth: '36px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                  {activeDocIdx + 1} / {docsInTab.length}
                </span>
                <button
                  type="button"
                  onClick={() => setActiveDocIdx(i => Math.min(docsInTab.length - 1, i + 1))}
                  disabled={activeDocIdx === docsInTab.length - 1}
                  style={{ width: '24px', height: '24px', borderRadius: '5px', border: '0.5px solid rgba(0,0,0,0.1)', background: 'white', cursor: activeDocIdx === docsInTab.length - 1 ? 'not-allowed' : 'pointer', opacity: activeDocIdx === docsInTab.length - 1 ? 0.4 : 1, fontSize: '12px', fontFamily: 'inherit' }}
                  aria-label="Suivant"
                >›</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                type="button"
                onClick={() => openLightboxOn(activeDoc)}
                title="Plein écran"
                style={{ width: '28px', height: '28px', borderRadius: '5px', border: '0.5px solid rgba(0,0,0,0.1)', background: 'white', color: '#5C6B7A', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' }}
              >⤢</button>
              <a
                href={activeDoc.download_url}
                download={activeDoc.nom_original}
                style={{ width: '28px', height: '28px', borderRadius: '5px', border: '0.5px solid rgba(0,0,0,0.1)', background: 'white', color: '#5C6B7A', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontSize: '13px' }}
                title="Télécharger"
              >⬇</a>
              <a
                href={activeDoc.download_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ width: '28px', height: '28px', borderRadius: '5px', border: '0.5px solid rgba(0,0,0,0.1)', background: 'white', color: '#5C6B7A', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontSize: '12px' }}
                title="Ouvrir dans un nouvel onglet"
              >↗</a>
              {canDeleteDoc && activeDoc.id && onDeleteAtt && (
                <button
                  type="button"
                  onClick={() => onDeleteAtt(activeDoc.id)}
                  title="Supprimer ce document"
                  style={{ width: '28px', height: '28px', borderRadius: '5px', border: '0.5px solid rgba(239,91,91,0.30)', background: 'white', color: '#ef5b5b', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' }}
                >✕</button>
              )}
            </div>
          </div>
          {/* Iframe PDF */}
          {activeDoc.download_url ? (
            <iframe
              src={activeDoc.download_url}
              title={activeDoc.nom_original || activeTab}
              style={{ width: '100%', height: '420px', border: 'none', background: '#525252', display: 'block' }}
            />
          ) : (
            <div style={{ height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '6px', color: '#8b8576', fontSize: '12px' }}>
              <span style={{ fontSize: '28px' }}>📄</span>
              Aperçu non disponible
            </div>
          )}
        </div>
      ) : (
        <div style={{
          padding: '20px',
          background: '#fafaf8',
          border: '1px dashed rgba(0,0,0,0.12)',
          borderRadius: '10px',
          textAlign: 'center',
          fontSize: '12.5px',
          color: '#8b8576',
        }}>
          Aucun document dans la catégorie <strong style={{ color: '#1d1d1f', fontWeight: 600 }}>{activeTab}</strong> pour ce dossier.
        </div>
      )}

      {/* Lightbox plein écran — ouvre au clic sur le nom du doc ou le bouton ⤢ */}
      {lightboxIdx !== null && (
        <DocLightbox
          docs={allDocsFlat}
          initial={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ModalPriceInput — input prix éditable inline pour la modale dossier
// Saisie en EUR HT (decimal), sauvegarde au blur ou Enter, Escape pour annuler.
// ═══════════════════════════════════════════════════════════════
function ModalPriceInput({ value, onSave, saving, disabled }) {
  const [val, setVal] = useState(value == null || value === '' ? '' : String(value));
  const [focused, setFocused] = useState(false);

  // Sync si la prop change (ex. refresh externe)
  useEffect(() => {
    if (!focused) setVal(value == null || value === '' ? '' : String(value));
  }, [value, focused]);

  const commit = () => {
    setFocused(false);
    const trimmed = (val || '').toString().replace(',', '.').trim();
    const num = trimmed === '' ? null : Number(trimmed);
    if (num !== null && Number.isNaN(num)) {
      setVal(value == null || value === '' ? '' : String(value));
      return;
    }
    const currentNum = value == null || value === '' ? null : Number(value);
    if (num === currentNum) return;
    onSave(num);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      background: '#fff',
      border: focused ? '1px solid #B8860B' : '0.5px solid var(--color-border-tertiary)',
      borderRadius: '5px',
      padding: '5px 8px',
      gap: '4px',
      opacity: disabled ? 0.5 : 1,
    }}>
      <input
        type="text"
        inputMode="decimal"
        value={val}
        placeholder="—"
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setVal(value == null || value === '' ? '' : String(value));
            setFocused(false);
            e.currentTarget.blur();
          }
        }}
        style={{
          flex: 1, minWidth: 0,
          border: 'none', outline: 'none', background: 'transparent',
          fontSize: '12.5px', color: '#1A1916',
          fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums',
          padding: 0,
        }}
      />
      <span style={{ fontSize: '11px', color: 'rgba(26,25,22,0.45)' }}>€</span>
      {saving && <span style={{ fontSize: '10px', color: '#B8860B' }}>…</span>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DETAIL MODAL — fiche détaillée (lecture pour client, édition admin/formaliste)
// ═══════════════════════════════════════════════════════════════

function DossierDetailModal({ dossier, onClose, formalistesMap, onUpdate, canEdit, catalog, currentUserId, currentUserRole, clientCanModify }) {
  // canModifyBasics = autorise statut + commentaire client + upload document
  //   - admin/formaliste/superviseur (canEdit) : toujours OK
  //   - client : OK SAUF si dossier verrouillé (Kbis reçu, Validé, Terminé)
  const canModifyBasics = canEdit || clientCanModify;
  // isClientLocked = utilisateur client + dossier verrouillé → afficher bannière "verrouillé"
  const isClientLocked = !canEdit && currentUserRole === 'client' && !clientCanModify;
  const [docs, setDocs] = useState([]);
  const [attachments, setAttachments] = useState({});
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState(null);
  // Upload manuel d'un attachment
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadType, setUploadType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [docTypes, setDocTypes] = useState([]);

  useEffect(() => {
    if (!dossier) return;
    setLoading(true);
    const token = localStorage.getItem('lexback_token');
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API_URL}/api/documents/dossier/${dossier.id}`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/api/documents/attachments/${dossier.id}`, { headers }).then(r => r.json()).catch(() => ({})),
      dossiersAPI.timeline(dossier.id).then(r => r.data).catch(() => []),
      fetch(`${API_URL}/api/documents/document-types`, { headers }).then(r => r.json()).catch(() => ({ types: [] })),
    ]).then(([d, atts, t, dt]) => {
      setDocs(Array.isArray(d) ? d : []);
      setAttachments(atts && typeof atts === 'object' && !atts.detail ? atts : {});
      setTimeline(Array.isArray(t) ? t : []);
      setDocTypes(dt?.types || []);
      setLoading(false);
    });
  }, [dossier]);

  // ESC pour fermer + scroll lock body quand drawer ouvert
  useEffect(() => {
    if (!dossier) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [dossier, onClose]);

  // Sub-types disponibles : filtrés par le type sélectionné
  // ⚠️ Doit rester AVANT le return null pour respecter les Rules of Hooks
  const availableSubTypes = useMemo(() => {
    if (!catalog || !dossier?.formality_type_id) return [];
    const type = (catalog.formality_types || []).find(t => t.id === dossier.formality_type_id);
    return type ? (type.sub_types || []) : [];
  }, [catalog, dossier?.formality_type_id]);

  const handleUpload = async () => {
    if (!uploadFiles.length || !uploadType) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('lexback_token');
      const headers = { Authorization: `Bearer ${token}` };
      const errors = [];
      // Upload séquentiel — tous les fichiers dans la même subcategory
      for (const file of uploadFiles) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          const url = `${API_URL}/api/documents/attachment/${dossier.id}?subcategory=${encodeURIComponent(uploadType)}`;
          const res = await fetch(url, {
            method: 'POST',
            headers,
            body: formData,
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            errors.push(`${file.name} : ${err.detail || 'erreur'}`);
          }
        } catch (e) {
          errors.push(`${file.name} : ${e.message}`);
        }
      }
      // Reload attachments
      const attsRes = await fetch(`${API_URL}/api/documents/attachments/${dossier.id}`, { headers });
      const atts = await attsRes.json();
      setAttachments(atts && typeof atts === 'object' && !atts.detail ? atts : {});
      if (errors.length > 0) {
        alert('Certains fichiers ont échoué : ' + errors.join(', '));
      }
      // Reset
      setUploadFiles([]);
      setUploadType('');
      setShowUpload(false);
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attId) => {
    if (!canEdit) return;
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      const token = localStorage.getItem('lexback_token');
      const res = await fetch(`${API_URL}/api/documents/attachment/${attId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erreur');
      // Reload
      const headers = { Authorization: `Bearer ${token}` };
      const attsRes = await fetch(`${API_URL}/api/documents/attachments/${dossier.id}`, { headers });
      const atts = await attsRes.json();
      setAttachments(atts && typeof atts === 'object' && !atts.detail ? atts : {});
    } catch (e) {
      alert('Erreur suppression');
    }
  };

  if (!dossier) return null;

  const frmInfo = formalistesMap?.[dossier.formaliste_id];
  const frmCount = frmInfo?.dossiers_actifs;

  // Champs autorisés pour un client (en plus de tout ce que canEdit autorise)
  // Liste blanche : statut, formalité, greffe, tarifs, commentaires. Bloqué dès
  // que le dossier est verrouillé (Kbis reçu / Validé / Terminé).
  const CLIENT_ALLOWED_FIELDS = new Set([
    'status_label_id',          // Statut
    'formality_type_id',         // Type de formalité
    'greffe_id',                 // Greffe
    'tarif_gtc',                 // Tarif GTC (HT)
    'tarif_jal',                 // Tarif JAL (HT)
    'elements_manquants',        // Commentaire / éléments à fournir
    'commentaire_client',        // Champ commentaire dédié (si présent)
  ]);
  const handleFieldChange = async (field, value) => {
    const allowed = canEdit || (clientCanModify && CLIENT_ALLOWED_FIELDS.has(field));
    if (!allowed) return;
    setSavingField(field);
    // Update optimiste : on met à jour le dossier local immédiatement
    // pour que le picker se rafraîchisse, même si le backend tarde.
    if (onUpdate) onUpdate({ ...dossier, [field]: value });
    try {
      const payload = { [field]: value };
      const res = await dossiersAPI.update(dossier.id, payload);
      if (onUpdate && res?.data) onUpdate(res.data);
    } catch (err) {
      // Le backend peut refuser certains champs côté client (ex. policy serveur).
      // On NE rollback PAS l'affichage : la modif reste visible côté client tant
      // que la policy backend n'est pas étendue. Erreur loggée en console pour debug.
      // (Si tu veux re-bloquer dur, remets `if (onUpdate) onUpdate(dossier);` ici
      //  et `alert(formatApiError(err, ...))` pour notifier l'utilisateur.)
      console.warn('[handleFieldChange] backend rejected', field, formatApiError(err));
    } finally {
      setSavingField(null);
    }
  };

  const handleSubTypesChange = async (subTypeIds) => {
    if (!canEdit) return;
    setSavingField('sub_type_ids');
    try {
      const res = await dossiersAPI.update(dossier.id, { sub_type_ids: subTypeIds });
      if (onUpdate) onUpdate(res.data);
    } catch (err) {
      alert(formatApiError(err, 'Erreur'));
    } finally {
      setSavingField(null);
    }
  };

  const InfoCard = ({ label, value, children }) => (
    <div style={{
      background:'#f5f1ea',
      border:'0.5px solid #e5e1d8',
      borderRadius:'8px',
      padding:'10px 12px',
      minWidth:0,
      overflow:'hidden',
    }}>
      <div style={{ fontSize:'9.5px', textTransform:'uppercase', color:'#9a8f7a', letterSpacing:'0.05em', marginBottom:'5px', fontWeight:600 }}>{label}</div>
      <div style={{
        fontSize:'12.5px', color:'#1a1a1a', fontWeight:500,
        wordBreak:'break-word', overflowWrap:'anywhere',
        lineHeight:1.4,
      }}>
        {children !== undefined ? children : (value || <span style={{ color:'#bfb5a3', fontWeight:400 }}>—</span>)}
      </div>
    </div>
  );

  // Catégories de pièces jointes affichées :
  // - canEdit (admin/formaliste/superviseur) : toutes les catégories
  // - client : seulement juridique + officiel (pas de justificatives ni internes Lexback)
  const attSections = canEdit ? [
    { key:'justificative', label:'Justificatives' },
    { key:'juridique',     label:'Juridiques' },
    { key:'officiel',      label:'Officiels' },
    { key:'lexback_internal', label:'Internes Lexback' },
  ] : [
    { key:'juridique', label:'Documents juridiques' },
    { key:'officiel',  label:'Documents officiels' },
  ];

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.45)',
          zIndex:9998,
          animation:'lbxFadeIn 0.18s ease-out',
        }}
      />
      {/* Drawer latéral droit */}
      <div
        className="lbx-drawer"
        onClick={e => e.stopPropagation()}
        style={{
          position:'fixed', top:0, right:0, bottom:0,
          width:'min(620px, 100vw)',
          backgroundColor:'#ffffff',
          backgroundImage:'none',
          borderLeft:'1px solid #e5e1d8',
          boxShadow:'-12px 0 40px rgba(0,0,0,0.18)',
          zIndex:9999,
          overflowY:'auto',
          animation:'lbxSlideIn 0.22s cubic-bezier(0.32, 0.72, 0, 1)',
          padding:'1.5rem 1.75rem 2rem',
          isolation:'isolate',
          opacity:1,
        }}
      >
        <style>{`
          @keyframes lbxSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
          @keyframes lbxFadeIn { from { opacity: 0; } to { opacity: 1; } }
          .lbx-drawer {
            background-color: #ffffff !important;
            background: #ffffff !important;
            opacity: 1 !important;
          }
        `}</style>

        {/* Header */}
        <div style={{
          display:'flex', justifyContent:'space-between', alignItems:'flex-start',
          marginBottom:'1.25rem',
          paddingBottom:'1rem',
          borderBottom:'1px solid #e5e1d8',
          position:'sticky', top:'-1.5rem', zIndex:5,
          backgroundColor:'#ffffff',
          margin:'-1.5rem -1.75rem 1.25rem',
          padding:'1.5rem 1.75rem 1rem',
        }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px', flexWrap:'wrap' }}>
              <span style={{ fontSize:'11px', color:'#B8860B', fontFamily:'JetBrains Mono, monospace', padding:'3px 8px', background:'rgba(184,134,11,0.08)', border:'0.5px solid rgba(184,134,11,0.20)', borderRadius:'4px' }}>{dossier.reference}</span>
              {canModifyBasics && catalog ? (
                <>
                  <InlineCatalogPicker
                    currentValue={dossier.formality_type_id}
                    options={catalog.formality_types || []}
                    onChange={(v) => handleFieldChange('formality_type_id', v)}
                    placeholder="+ Type"
                  />
                  <InlineCatalogPicker
                    currentValue={dossier.status_label_id}
                    options={catalog.status_labels || []}
                    onChange={(v) => handleFieldChange('status_label_id', v)}
                    placeholder="+ Statut"
                  />
                </>
              ) : (
                <>
                  {dossier.formality_type_label
                    ? <CatalogPill label={dossier.formality_type_label.name} color={dossier.formality_type_label.color} size="sm" />
                    : <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'4px', background:'rgba(184,134,11,0.1)', color:'#B8860B', fontWeight:500 }}>{dossier.type?.replace(/_/g, ' ')}</span>
                  }
                  {dossier.status_label
                    ? <CatalogPill label={dossier.status_label.name} color={dossier.status_label.color} emoji={dossier.status_label.emoji} size="sm" />
                    : <StatusFallbackBadge status={dossier.status} />
                  }
                </>
              )}
            </div>
            <div style={{ fontSize:'18px', fontWeight:600, color:'#1a1a1a', lineHeight:1.2, wordBreak:'break-word' }}>{dossier.societe_nom || '—'}</div>
            {dossier.societe_siren && <div style={{ fontSize:'12px', color:'#9a8f7a', marginTop:'3px', fontFamily:'JetBrains Mono, monospace' }}>SIREN {dossier.societe_siren}</div>}
          </div>
          <button
            onClick={onClose}
            title="Fermer (Échap)"
            style={{
              background:'transparent', border:'1px solid #e5e1d8', borderRadius:'8px',
              cursor:'pointer', fontSize:'18px', color:'#9a8f7a',
              width:'32px', height:'32px', display:'flex', alignItems:'center', justifyContent:'center',
              marginLeft:'1rem', flexShrink:0, lineHeight:1,
            }}
            onMouseEnter={e => { e.currentTarget.style.background='#f5f1ea'; e.currentTarget.style.color='#1a1a1a'; }}
            onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#9a8f7a'; }}
          >×</button>
        </div>

        {/* Edit panel pour admin/formaliste */}
        {canEdit && catalog && (
          <div style={{ background:'rgba(184,134,11,0.04)', border:'0.5px solid rgba(184,134,11,0.20)', borderRadius:'8px', padding:'12px', marginBottom:'12px' }}>
            <div style={{ fontSize:'10px', textTransform:'uppercase', color:'#B8860B', fontWeight:600, marginBottom:'8px', letterSpacing:'0.05em' }}>
              Propriétés {savingField && <span style={{ marginLeft:'8px', fontWeight:400, fontStyle:'italic' }}>· enregistrement…</span>}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
              <div style={{ gridColumn:'1 / -1' }}>
                <div style={{ fontSize:'10px', color:'var(--color-text-tertiary)', marginBottom:'3px' }}>Sous-types ({availableSubTypes.length} disponibles)</div>
                <InlineCatalogPicker
                  currentValue={(dossier.sub_type_labels || []).map(s => s.id)}
                  options={availableSubTypes}
                  multi
                  onChange={handleSubTypesChange}
                  placeholder={availableSubTypes.length === 0 ? 'Choisir un type d\'abord' : '+ Ajouter sous-types'}
                  disabled={availableSubTypes.length === 0}
                />
              </div>
              <div>
                <div style={{ fontSize:'10px', color:'var(--color-text-tertiary)', marginBottom:'3px' }}>Greffe</div>
                <InlineCatalogPicker
                  currentValue={dossier.greffe_id}
                  options={catalog.greffes || []}
                  onChange={(v) => handleFieldChange('greffe_id', v)}
                />
              </div>
              <div>
                <div style={{ fontSize:'10px', color:'var(--color-text-tertiary)', marginBottom:'3px' }}>Cabinet BtoB</div>
                <InlineCatalogPicker
                  currentValue={dossier.cabinet_id}
                  options={catalog.cabinets || []}
                  onChange={(v) => handleFieldChange('cabinet_id', v)}
                />
              </div>
              <div>
                <div style={{ fontSize:'10px', color:'var(--color-text-tertiary)', marginBottom:'3px' }}>Tarif GTC (HT)</div>
                <ModalPriceInput
                  value={dossier.tarif_gtc}
                  saving={savingField === 'tarif_gtc'}
                  onSave={(v) => handleFieldChange('tarif_gtc', v)}
                />
              </div>
              <div>
                <div style={{ fontSize:'10px', color:'var(--color-text-tertiary)', marginBottom:'3px' }}>Tarif JAL (HT)</div>
                <ModalPriceInput
                  value={dossier.tarif_jal}
                  saving={savingField === 'tarif_jal'}
                  onSave={(v) => handleFieldChange('tarif_jal', v)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Edit panel pour le CLIENT (si dossier non verrouillé) */}
        {!canEdit && clientCanModify && catalog && (
          <div style={{ background:'rgba(26,85,160,0.04)', border:'0.5px solid rgba(26,85,160,0.20)', borderRadius:'8px', padding:'12px', marginBottom:'12px' }}>
            <div style={{ fontSize:'10px', textTransform:'uppercase', color:'#1A55A0', fontWeight:600, marginBottom:'8px', letterSpacing:'0.05em' }}>
              Modifier mon dossier {savingField && <span style={{ marginLeft:'8px', fontWeight:400, fontStyle:'italic' }}>· enregistrement…</span>}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', alignItems:'end' }}>
              <div>
                <div style={{ fontSize:'10px', color:'var(--color-text-tertiary)', marginBottom:'3px' }}>Greffe</div>
                <InlineCatalogPicker
                  currentValue={dossier.greffe_id}
                  options={catalog.greffes || []}
                  onChange={(v) => handleFieldChange('greffe_id', v)}
                />
              </div>
              <div>
                <div style={{ fontSize:'10px', color:'var(--color-text-tertiary)', marginBottom:'3px' }}>Tarif GTC (HT)</div>
                <ModalPriceInput
                  value={dossier.tarif_gtc}
                  saving={savingField === 'tarif_gtc'}
                  onSave={(v) => handleFieldChange('tarif_gtc', v)}
                />
              </div>
              <div>
                <div style={{ fontSize:'10px', color:'var(--color-text-tertiary)', marginBottom:'3px' }}>Tarif JAL (HT)</div>
                <ModalPriceInput
                  value={dossier.tarif_jal}
                  saving={savingField === 'tarif_jal'}
                  onSave={(v) => handleFieldChange('tarif_jal', v)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Bannière si dossier verrouillé pour le client */}
        {isClientLocked && (
          <div style={{
            background:'rgba(26,122,74,0.06)',
            border:'0.5px solid rgba(26,122,74,0.20)',
            borderRadius:'8px', padding:'10px 12px', marginBottom:'12px',
            display:'flex', alignItems:'center', gap:'8px',
          }}>
            <span style={{ fontSize:'14px' }}>🔒</span>
            <div style={{ fontSize:'12px', color:'#1A7A4A', lineHeight:1.4 }}>
              Ce dossier est <strong>validé / clôturé</strong> — il n'est plus modifiable.
              Vous pouvez toujours consulter les documents et échanger via la discussion.
            </div>
          </div>
        )}

        {/* Ligne d'identité compacte (visible client + Lexback) */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px 18px',
          alignItems: 'center',
          padding: '0 0 14px',
          marginBottom: '14px',
          borderBottom: '0.5px solid rgba(0,0,0,0.06)',
          fontSize: '12.5px',
          color: '#5C6B7A',
        }}>
          {dossier.created_at && (
            <span>
              <span style={{ color:'#8b8576', fontSize:'10.5px', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Date </span>
              {canEdit ? (
                <input
                  type="date"
                  value={new Date(dossier.created_at).toISOString().slice(0, 10)}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={async (e) => {
                    const newDate = e.target.value;
                    if (!newDate) return;
                    try {
                      setSavingField('created_at');
                      await dossiersAPI.update(dossier.id, { created_at: newDate });
                      // Notifier parent pour refresh
                      if (onUpdate) onUpdate();
                    } catch (err) {
                      console.error('Update date failed:', err);
                      alert('Impossible de modifier la date');
                    } finally {
                      setSavingField(null);
                    }
                  }}
                  style={{
                    background:'transparent', border:'none',
                    color:'#5C6B7A', fontSize:'12.5px',
                    fontFamily:'inherit', cursor:'pointer',
                    padding:'1px 4px', borderRadius:'4px',
                    fontVariantNumeric:'tabular-nums',
                    outline:'none',
                  }}
                  title="Cliquer pour modifier la date du dossier"
                />
              ) : (
                new Date(dossier.created_at).toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' })
              )}
              {savingField === 'created_at' && <span style={{ marginLeft:6, fontSize:'10px', color:'#B8860B' }}>…</span>}
            </span>
          )}
          {!canModifyBasics && (dossier.greffe_label?.name || dossier.greffe) && (
            <span><span style={{ color:'#8b8576', fontSize:'10.5px', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Greffe </span>{dossier.greffe_label?.name || dossier.greffe}</span>
          )}
          {!canModifyBasics && (dossier.cabinet_label?.name || dossier.btob_cabinet) && (
            <span><span style={{ color:'#8b8576', fontSize:'10.5px', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Cabinet </span>{dossier.cabinet_label?.name || dossier.btob_cabinet}</span>
          )}
          {dossier.formaliste_nom && (
            <span><span style={{ color:'#8b8576', fontSize:'10.5px', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Formaliste </span>{dossier.formaliste_nom}</span>
          )}
          {dossier.commercial && (
            <span><span style={{ color:'#8b8576', fontSize:'10.5px', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Commercial </span>{dossier.commercial}</span>
          )}
          {dossier.email_client_final && (
            <span><span style={{ color:'#8b8576', fontSize:'10.5px', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Email </span><a href={`mailto:${dossier.email_client_final}`} style={{ color:'#5C6B7A' }}>{dossier.email_client_final}</a></span>
          )}
          {dossier.etat_facturation && (
            <span><span style={{ color:'#8b8576', fontSize:'10.5px', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600 }}>Facturation </span>{dossier.etat_facturation}</span>
          )}
        </div>

        {/* Formulaire d'upload — déclenché par le bouton "+ Ajouter un document" dans DocsPreviewSection.
            L'ancienne liste plate des documents a été supprimée (elle faisait doublon avec l'aperçu). */}
        {!loading && showUpload && (
          <div style={{ marginBottom:'12px' }}>
            <div style={{ background:'var(--color-background-secondary)', border:'1px solid var(--color-border-tertiary)', borderRadius:'8px', padding:'12px', marginBottom:'12px' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'10px' }}>
                <div>
                  <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--color-text-tertiary)', marginBottom:'4px' }}>Type de document</div>
                  <select
                    value={uploadType}
                    onChange={e => setUploadType(e.target.value)}
                    style={{ width:'100%', padding:'7px 10px', background:'var(--color-background-primary)', border:'1px solid var(--color-border-tertiary)', borderRadius:'6px', fontSize:'12px', color:'var(--color-text-primary)' }}
                  >
                    <option value="">— Choisir un type —</option>
                    {(() => {
                      const groups = {};
                      docTypes.forEach(t => {
                        const cat = t.category;
                        // Le client (non canEdit) ne voit jamais les types "Interne Lexback"
                        if (!canEdit && cat === 'lexback_internal') return;
                        if (!groups[cat]) groups[cat] = [];
                        groups[cat].push(t);
                      });
                      const labels = { juridique:'Juridique', officiel:'Officiel', justificative:'Justificative', lexback_internal:'Interne Lexback' };
                      return Object.keys(groups).map(cat => (
                        <optgroup key={cat} label={labels[cat] || cat}>
                          {groups[cat].map(t => <option key={t.label} value={t.label}>{t.label}</option>)}
                        </optgroup>
                      ));
                    })()}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.05em', color:'var(--color-text-tertiary)', marginBottom:'4px' }}>Fichiers (PDF/JPG/PNG, max 50MB) — sélection multiple possible</div>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={e => setUploadFiles(Array.from(e.target.files || []))}
                    style={{ width:'100%', padding:'5px', background:'var(--color-background-primary)', border:'1px solid var(--color-border-tertiary)', borderRadius:'6px', fontSize:'11px', color:'var(--color-text-primary)' }}
                  />
                  {uploadFiles.length > 0 && (
                    <div style={{ marginTop:'6px', display:'flex', flexDirection:'column', gap:'3px' }}>
                      {uploadFiles.map((f, i) => (
                        <div key={i} style={{
                          display:'flex', alignItems:'center', justifyContent:'space-between',
                          fontSize:'11px', color:'var(--color-text-secondary)',
                          padding:'4px 8px', background:'var(--color-background-primary)',
                          border:'1px solid var(--color-border-tertiary)', borderRadius:'4px',
                        }}>
                          <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
                          <button
                            onClick={() => setUploadFiles(uploadFiles.filter((_, idx) => idx !== i))}
                            style={{ background:'transparent', border:'none', color:'var(--color-text-tertiary)', cursor:'pointer', padding:'0 4px', fontSize:'14px', flexShrink:0 }}
                            title="Retirer"
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', gap:'6px' }}>
                <button
                  onClick={() => { setShowUpload(false); setUploadFiles([]); setUploadType(''); }}
                  disabled={uploading}
                  style={{ background:'transparent', border:'1px solid var(--color-border-tertiary)', color:'var(--color-text-secondary)', padding:'6px 14px', borderRadius:'6px', fontSize:'11px', cursor:'pointer' }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploadFiles.length === 0 || !uploadType || uploading}
                  style={{
                    background: uploadFiles.length === 0 || !uploadType || uploading ? 'var(--color-background-secondary)' : '#5b8def',
                    color: uploadFiles.length === 0 || !uploadType || uploading ? 'var(--color-text-tertiary)' : '#fff',
                    border: 'none', padding:'6px 14px', borderRadius:'6px', fontSize:'11px',
                    cursor: uploadFiles.length === 0 || !uploadType || uploading ? 'not-allowed' : 'pointer',
                    fontWeight: 500,
                  }}
                >
                  {uploading ? 'Upload en cours...' : (uploadFiles.length > 1 ? `Envoyer ${uploadFiles.length} fichiers` : 'Envoyer')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DOCUMENTS — vue 4 onglets fixes (Statuts/PV/Kbis/RBE/Cession/Autres) avec iframe preview.
            Inclut le bouton "+ Ajouter un document" et la suppression du doc actif si autorisé.
            Visible pour TOUS les utilisateurs (l'ancienne liste plate sous est désormais supprimée
            pour éviter le doublon avec l'aperçu). */}
        <DocsPreviewSection
          dossier={dossier}
          attachments={attachments}
          currentUserRole={currentUserRole}
          canAddDoc={canModifyBasics}
          canDeleteDoc={canEdit}
          isUploadOpen={showUpload}
          onAddDocClick={() => setShowUpload(s => !s)}
          onDeleteAtt={handleDeleteAttachment}
        />

        {/* Elements manquants — éditable pour formalistes/admins ET client (si non verrouillé), lecture sinon */}
        {canModifyBasics ? (
          <div style={{ borderLeft:'3px solid #f59e0b', background:'rgba(245,158,11,0.06)', borderRadius:'0 6px 6px 0', padding:'10px 12px', marginBottom:'12px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px' }}>
              <div style={{ fontSize:'10px', fontWeight:600, textTransform:'uppercase', color:'#f59e0b' }}>
                {canEdit ? <>Éléments manquants <span style={{ fontWeight:400, fontStyle:'italic', textTransform:'none' }}>(visible client)</span></> : 'Commentaire / éléments à fournir'}
              </div>
              {savingField === 'elements_manquants' && <span style={{ fontSize:'10px', color:'#f59e0b' }}>Enregistrement…</span>}
            </div>
            <textarea
              defaultValue={dossier.elements_manquants || ''}
              placeholder={canEdit
                ? 'Listez ici les pièces ou informations manquantes que le client doit fournir…'
                : 'Ajoutez ici un commentaire pour l\'équipe Lexback ou les pièces que vous allez transmettre…'}
              onBlur={async (e) => {
                const newVal = e.target.value;
                if (newVal === (dossier.elements_manquants || '')) return;
                try {
                  setSavingField('elements_manquants');
                  await dossiersAPI.update(dossier.id, { elements_manquants: newVal });
                  if (onUpdate) onUpdate();
                } catch (err) {
                  console.error('Update elements_manquants failed:', err);
                  alert('Impossible d\'enregistrer le commentaire');
                } finally {
                  setSavingField(null);
                }
              }}
              rows={3}
              style={{
                width:'100%', background:'transparent', border:'none',
                fontSize:'12px', fontFamily:'inherit', color:'var(--color-text-primary)',
                outline:'none', resize:'vertical', minHeight:'42px', lineHeight:1.5,
                padding:0,
              }}
            />
          </div>
        ) : dossier.elements_manquants ? (
          <div style={{ borderLeft:'3px solid #f59e0b', background:'rgba(245,158,11,0.06)', borderRadius:'0 6px 6px 0', padding:'10px 12px', marginBottom:'12px' }}>
            <div style={{ fontSize:'10px', fontWeight:600, textTransform:'uppercase', color:'#f59e0b', marginBottom:'4px' }}>Éléments manquants</div>
            <div style={{ fontSize:'12px', color:'var(--color-text-primary)', whiteSpace:'pre-wrap' }}>{dossier.elements_manquants}</div>
          </div>
        ) : null}

        {/* Notes formaliste (caché du client — éditable pour formalistes/admins) */}
        {canEdit && (
          <div style={{ borderLeft:'3px solid #B8860B', background:'rgba(184,134,11,0.06)', borderRadius:'0 6px 6px 0', padding:'10px 12px', marginBottom:'12px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'4px' }}>
              <div style={{ fontSize:'10px', fontWeight:600, textTransform:'uppercase', color:'#B8860B' }}>Notes formaliste <span style={{ fontWeight:400, fontStyle:'italic', textTransform:'none' }}>(privé Lexback)</span></div>
              {savingField === 'notes_formaliste_lexback' && <span style={{ fontSize:'10px', color:'#B8860B' }}>Enregistrement…</span>}
            </div>
            <textarea
              defaultValue={dossier.notes_formaliste_lexback || ''}
              placeholder="Notes internes Lexback — invisible pour le client…"
              onBlur={async (e) => {
                const newVal = e.target.value;
                if (newVal === (dossier.notes_formaliste_lexback || '')) return;
                try {
                  setSavingField('notes_formaliste_lexback');
                  await dossiersAPI.update(dossier.id, { notes_formaliste_lexback: newVal });
                  if (onUpdate) onUpdate();
                } catch (err) {
                  console.error('Update notes failed:', err);
                  alert('Impossible d\'enregistrer les notes');
                } finally {
                  setSavingField(null);
                }
              }}
              rows={3}
              style={{
                width:'100%', background:'transparent', border:'none',
                fontSize:'12px', fontFamily:'inherit', color:'var(--color-text-primary)',
                outline:'none', resize:'vertical', minHeight:'42px', lineHeight:1.5,
                padding:0,
              }}
            />
          </div>
        )}

        {/* Discussion — titre adaptatif selon le rôle de l'utilisateur connecté :
            - Si le user est un CLIENT et un formaliste est assigné : "Discussion avec <Prénom>" (juste le prénom du formaliste)
            - Sinon (admin / formaliste / pas d'assignation) : "Discussion avec le client" */}
        {(() => {
          let discussionTitle = 'Discussion avec le client';
          if (currentUserRole === 'client' && dossier.formaliste_nom) {
            // Ex: "Rita AMRI" → "Rita"
            const prenom = dossier.formaliste_nom.trim().split(/\s+/)[0] || '';
            if (prenom) discussionTitle = `Discussion avec ${prenom}`;
          }
          return (
            <DiscussionSection
              dossierId={dossier.id}
              currentUserId={currentUserId}
              currentUserRole={currentUserRole}
              canEdit={canEdit}
              forcedVisibility="shared"
              title={discussionTitle}
            />
          );
        })()}

        {/* Notes internes Lexback — visible UNIQUEMENT par les utilisateurs internes */}
        {canEdit && (
          <DiscussionSection
            dossierId={dossier.id}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            canEdit={canEdit}
            forcedVisibility="internal"
            title="Notes internes Lexback"
          />
        )}

        {/* Timeline */}
        {timeline.length > 0 && (
          <div style={{ marginBottom:'12px' }}>
            <div style={{ fontWeight:500, fontSize:'12px', color:'var(--color-text-primary)', marginBottom:'8px' }}>Historique</div>
            <div style={{ borderLeft:'2px solid rgba(184,134,11,0.2)', paddingLeft:'12px' }}>
              {timeline.slice(0, 15).map((ev, i) => (
                <div key={i} style={{ position:'relative', paddingBottom:'8px' }}>
                  <div style={{ position:'absolute', left:'-17px', top:'4px', width:'6px', height:'6px', borderRadius:'50%', background:'#B8860B' }} />
                  <div style={{ fontSize:'10px', color:'var(--color-text-tertiary)' }}>
                    {new Date(ev.timestamp).toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' })} — {ev.actor || 'Système'} · {ev.content || ev.type}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', marginTop:'0.5rem' }}>
          <button onClick={onClose} style={{ background:'transparent', border:'0.5px solid var(--color-border-secondary)', borderRadius:'8px', padding:'6px 16px', fontSize:'12px', color:'var(--color-text-secondary)', cursor:'pointer' }}>Fermer</button>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// BULK ACTIONS BAR
// ═══════════════════════════════════════════════════════════════

function BulkActionsBar({ selectedIds, onClear, catalog, onBulkUpdate }) {
  const [statusOpen, setStatusOpen] = useState(false);
  const count = selectedIds.length;

  if (count === 0) return null;

  return (
    <div style={{
      background:'rgba(184,134,11,0.08)',
      border:'0.5px solid rgba(184,134,11,0.30)',
      borderRadius:'8px',
      padding:'8px 12px',
      marginBottom:'12px',
      display:'flex',
      justifyContent:'space-between',
      alignItems:'center',
      flexWrap:'wrap',
      gap:'8px',
    }}>
      <span style={{ fontSize:'12px', color:'#B8860B', fontWeight:500 }}>
        {count} dossier{count > 1 ? 's' : ''} sélectionné{count > 1 ? 's' : ''}
      </span>
      <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
        <span style={{ fontSize:'11px', color:'var(--color-text-secondary)', marginRight:'4px' }}>Changer le statut →</span>
        <InlineCatalogPicker
          currentValue={null}
          options={catalog?.status_labels || []}
          onChange={(statusLabelId) => {
            if (statusLabelId && window.confirm(`Appliquer ce statut à ${count} dossier(s) ?`)) {
              onBulkUpdate({ status_label_id: statusLabelId });
            }
          }}
          placeholder="Choisir statut"
        />
        <button onClick={onClear} style={{
          background:'transparent', border:'0.5px solid var(--color-border-secondary)',
          padding:'4px 12px', borderRadius:'5px', fontSize:'11px', cursor:'pointer',
          color:'var(--color-text-secondary)',
        }}>Annuler</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN DOSSIERS PAGE
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// VOLET DE CHAT RAPIDE — ouvre les échanges d'un dossier au clic sur 💬,
// sans charger toute la fiche. Réutilise DiscussionSection :
//   - "Entre collaborateurs" (interne, si droits d'édition)
//   - "Avec le client" (partagé)
// ═══════════════════════════════════════════════════════════════
function QuickChatDrawer({ dossier, onClose, currentUserId, currentUserRole, canEdit }) {
  useEffect(() => {
    if (!dossier) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dossier, onClose]);

  if (!dossier) return null;

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.18)', zIndex:1000 }} />
      <div style={{
        position:'fixed', top:0, right:0, height:'100vh', width:'min(440px, 92vw)',
        background:'var(--color-background-primary, #ffffff)',
        borderLeft:'0.5px solid rgba(0,0,0,0.10)',
        boxShadow:'-8px 0 30px rgba(0,0,0,0.12)', zIndex:1001,
        display:'flex', flexDirection:'column', animation:'lbDrawerIn 0.18s ease',
      }}>
        <style>{`@keyframes lbDrawerIn{from{transform:translateX(24px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* En-tête */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'10px', padding:'16px 18px', borderBottom:'0.5px solid rgba(0,0,0,0.07)' }}>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.06em', color:'#8b8576', fontWeight:600, marginBottom:'3px' }}>Échanges</div>
            <div style={{ fontSize:'15px', fontWeight:500, color:'#1d1d1f', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{dossier.societe_nom || '—'}</div>
            {dossier.societe_siren && (
              <div style={{ fontSize:'11px', color:'#8b8576', fontVariantNumeric:'tabular-nums' }}>{dossier.societe_siren}</div>
            )}
          </div>
          <button onClick={onClose} title="Fermer" style={{ flexShrink:0, width:'28px', height:'28px', borderRadius:'6px', border:'0.5px solid rgba(0,0,0,0.10)', background:'white', color:'#5C6B7A', cursor:'pointer', fontSize:'16px', lineHeight:1, fontFamily:'inherit' }}>×</button>
        </div>

        {/* Corps : fils de discussion */}
        <div style={{ flex:1, overflowY:'auto', padding:'14px 18px' }}>
          {canEdit && (
            <DiscussionSection
              dossierId={dossier.id}
              currentUserId={currentUserId}
              currentUserRole={currentUserRole}
              canEdit={canEdit}
              forcedVisibility="internal"
              title="Entre collaborateurs"
            />
          )}
          <DiscussionSection
            dossierId={dossier.id}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            canEdit={canEdit}
            forcedVisibility="shared"
            title="Avec le client"
          />
        </div>
      </div>
    </>
  );
}

export default function Dossiers() {
  const { user } = useAuth();
  const { catalog, loading: catalogLoading } = useCatalog();
  const isClient = user?.role === 'client';
  const canEdit = !isClient && ['admin', 'formaliste', 'superviseur'].includes(user?.role);

  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Notifs non lues par dossier_id pour afficher le badge dans la liste
  // Format : { [dossier_id]: { count: N, types: ['note', 'status_change', ...] } }
  const [unreadByDossier, setUnreadByDossier] = useState({});
  // ── Résumé des documents par dossier (alimenté en background pour les indicateurs colonnes) ──
  // Forme : { [dossier_id]: { Statuts: 1, PV: 0, Kbis: 1, RBE: 0 } }
  const [attachmentsSummary, setAttachmentsSummary] = useState({});
  const [filterStatus, setFilterStatus] = useState('tous');           // bucket interne
  const [filterStatusLabels, setFilterStatusLabels] = useState([]);   // ids de status_labels
  const [filterFrm, setFilterFrm] = useState('');
  const [filterCabinet, setFilterCabinet] = useState('');
  const [filterFormalityType, setFilterFormalityType] = useState('');
  // Filtre additionnel via query string (?status=doc_manquant ou ?status=rejete)
  // Permet aux pills "Actions requises" du Dashboard d'amener directement
  // sur la liste filtrée. Utilisateur peut clear via le bouton dédié.
  const location = useLocation();
  const [filterRawStatus, setFilterRawStatus] = useState('');
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get('status') || '';
    setFilterRawStatus(s);
    // Si une query status arrive, on remet le bucket sur "tous" pour éviter
    // qu'un autre filtre l'écrase
    if (s) setFilterStatus('tous');
  }, [location.search]);
  const [search, setSearch] = useState('');
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [chatDossier, setChatDossier] = useState(null);
  const [sendModal, setSendModal] = useState(null); // { dossier, mode }
  const [selectedIds, setSelectedIds] = useState([]);
  const [formalistes, setFormalistes] = useState([]);

  // ── Ligne d'ajout inline (style Airtable) ──
  // Lorsque l'utilisateur clique sur "+ Ajouter une ligne", on affiche une row éditable
  // en haut de la table. La création passe par le flow brouillon (draftAPI) pour rester
  // compatible avec les exigences du backend (champ `type` requis, etc.) :
  //   1) draftAPI.create({siren, is_creation, type_formalite})
  //   2) draftAPI.upload(id, formData) pour chaque fichier
  //   3) draftAPI.submit(id, {type_formalite, direct_submit:true, ...})
  const [inlineAdd, setInlineAdd] = useState(null); // { societe_nom, societe_siren, type_main, formality_type_id, files } ou null
  // Styles réutilisables du panneau d'ajout rapide (compléments) — contraste renforcé
  const _qaCard = { background:'#fff', border:'0.5px solid #e6e0d2', borderRadius:'10px', padding:'13px 14px', boxShadow:'0 1px 2px rgba(0,0,0,0.03)' };
  const _qaHead = { fontSize:'11px', fontWeight:700, color:'#5a4d2e', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'10px', display:'flex', alignItems:'center', gap:'7px' };
  const _qaDot = { display:'inline-block', width:'6px', height:'6px', borderRadius:'50%', background:'#B8860B', flexShrink:0 };
  const _qaSubLbl = { fontSize:'10px', fontWeight:600, color:'#8a7f6a', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'4px' };
  const _qaField = { width:'100%', border:'1px solid #d8d1c0', borderRadius:'7px', padding:'8px 10px', fontSize:'12.5px', outline:'none', fontFamily:'inherit', background:'#fff', color:'#2a2620' };
  const _qaBadge = { background:'#B8860B', color:'#fff', borderRadius:'999px', fontSize:'10px', fontWeight:700, padding:'1px 7px', marginLeft:'4px' };
  const [creatingInline, setCreatingInline] = useState(false);
  // Auto-lookup Pappers dès qu'un SIREN à 9 chiffres est saisi
  // pappersInfo = { loading, error, societe: {nom, forme, capital, adresse, statut}, siren }
  const [pappersInfo, setPappersInfo] = useState({ loading: false, error: '', societe: null, siren: '' });

  // ── Lightbox plein écran au clic sur les pills de docs dans la table ──
  // Forme : { docs: [...], initial: 0 } | null
  const [tableLightbox, setTableLightbox] = useState(null);
  const [loadingPillFor, setLoadingPillFor] = useState(null); // dossierId:cat pendant fetch

  // Ouvre la lightbox sur la catégorie `cat` du dossier `d` :
  // fetch les attachments du dossier puis dérive la liste plate de la catégorie.
  const openDocsForCategory = async (d, cat) => {
    const key = `${d.id}:${cat}`;
    setLoadingPillFor(key);
    try {
      const token = localStorage.getItem('lexback_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/documents/attachments/${d.id}`, { headers });
      if (!res.ok) throw new Error('Fetch attachments failed');
      const atts = await res.json();
      // groupAttachmentsByTab existe en haut du fichier — on l'utilise pour dériver la cat.
      const buckets = groupAttachmentsByTab(atts || {}, user?.role);
      const list = buckets[cat] || [];
      if (list.length === 0) {
        alert(`Aucun document dans la catégorie "${cat}" pour ce dossier.`);
        return;
      }
      setTableLightbox({ docs: list, initial: 0 });
    } catch (e) {
      console.error('[openDocsForCategory]', e);
      alert('Impossible de charger les documents.');
    } finally {
      setLoadingPillFor(null);
    }
  };

  // ── Effet : fetch Pappers automatique sur SIREN valide (debounced 500ms) ──
  useEffect(() => {
    const siren = (inlineAdd?.societe_siren || '').replace(/\D/g, '');
    if (!inlineAdd || siren.length !== 9) {
      setPappersInfo({ loading: false, error: '', societe: null, siren: '' });
      return;
    }
    // Si on a déjà fetché ce SIREN, ne pas redemander
    if (pappersInfo.siren === siren && (pappersInfo.societe || pappersInfo.error)) return;

    setPappersInfo(p => ({ ...p, loading: true, error: '', siren }));
    const tid = setTimeout(async () => {
      try {
        const res = await dossiersAPI.pappersLookup(siren);
        const data = res.data || {};
        const siege = data.siege || {};
        let adresse = siege.adresse || '';
        if (siege.code_postal || siege.ville) {
          adresse += (adresse ? ', ' : '') + [siege.code_postal, siege.ville].filter(Boolean).join(' ');
        }
        const societe = {
          nom: data.nom || data.denomination || '',
          forme: data.forme_juridique || '',
          capital: data.capital ? `${Number(data.capital).toLocaleString('fr-FR')} €` : '',
          adresse,
          statut: data.statut || 'Actif',
        };
        setPappersInfo({ loading: false, error: '', societe, siren });
        // Auto-pré-remplir le nom de la société si vide
        if (societe.nom) {
          setInlineAdd(prev => prev && !prev.societe_nom ? { ...prev, societe_nom: societe.nom } : prev);
        }
      } catch (err) {
        console.error('[Pappers lookup]', err);
        setPappersInfo({ loading: false, error: 'Société introuvable', societe: null, siren });
      }
    }, 500);
    return () => clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inlineAdd?.societe_siren]);

  // ── Téléchargement Pappers (Statuts / Extrait RNE / Avis INSEE) ──
  // Le backend renvoie un blob PDF.
  const downloadPappersDoc = async (siren, kind, label) => {
    try {
      const res = await api.get(`/api/dossiers/pappers/${siren}/${kind}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${kind}_${siren}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('[Pappers download]', err);
      alert(`Téléchargement ${label} indisponible pour ce SIREN`);
    }
  };


  // ── Filtres par colonne (saisie inline sous l'entête) ──
  const [colFilters, setColFilters] = useState({
    societe: '',         // texte (nom + SIREN)
    type: '',            // id du formality_type
    subtypes: '',        // texte ou id de sub-type
    statut: '',          // id du status_label OU bucket interne
    reference: '',       // texte
    dateFrom: '',        // YYYY-MM-DD
    dateTo: '',          // YYYY-MM-DD
    formaliste: '',      // id formaliste
    greffe: '',          // id greffe
    cabinet: '',         // id cabinet
    coutMin: '',         // nombre
    coutMax: '',         // nombre
  });
  const setColFilter = (key, val) => setColFilters(f => ({ ...f, [key]: val }));
  const resetColFilters = () => setColFilters({
    societe: '', type: '', subtypes: '', statut: '', reference: '',
    dateFrom: '', dateTo: '', formaliste: '', greffe: '', cabinet: '',
    coutMin: '', coutMax: '',
  });
  const hasActiveColFilter = Object.values(colFilters).some(v => v !== '' && v !== null && v !== undefined);

  // ── Tri par colonne ──
  // sortKey: 'societe' | 'type' | 'statut' | 'reference' | 'date' | 'formaliste' | 'greffe' | 'cabinet' | 'cout'
  // sortDir: 'asc' | 'desc'
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  useEffect(() => {
    loadDossiers();
    if (canEdit) {
      teamAPI.formalistes().then(r => setFormalistes(r.data || [])).catch(() => {});
    }
  }, [canEdit]);

  const loadDossiers = async () => {
    setLoading(true);
    try {
      const res = await dossiersAPI.list({ limit: 500 });
      setDossiers(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // ── Suppression d'un dossier (autorisée tant qu'il n'est pas parti en signature) ──
  const handleDeleteDossier = async (d) => {
    const nom = d.societe_nom || d.reference || 'ce dossier';
    if (!window.confirm(`Supprimer définitivement le dossier « ${nom} » ?\nCette action est irréversible.`)) return;
    try {
      await dossiersAPI.remove(d.id);
      setDossiers(list => list.filter(x => x.id !== d.id));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      window.alert(typeof detail === 'string' ? detail : "Suppression impossible — réessaie ou recharge la page.");
    }
  };

  // ── Charge les notifs non lues pour afficher un badge sur les lignes dossier ──
  // Backend : /api/notifications?unread_only=true&limit=200
  // Format réponse attendu : [{ id, type, dossier_id, ... }]
  // Map de sortie : { [dossier_id]: { count: N, types: ['note','status_change','message',...] } }
  const loadUnreadNotifs = useCallback(async () => {
    try {
      const token = localStorage.getItem('lexback_token');
      if (!token) return;
      const r = await fetch(`${API_URL}/api/notifications?unread_only=true&limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return; // endpoint absent ou erreur — on ignore silencieusement
      const data = await r.json();
      const list = Array.isArray(data) ? data : (data?.items || []);
      const map = {};
      for (const n of list) {
        const did = n.dossier_id || n.dossierId;
        if (!did) continue;
        if (!map[did]) map[did] = { count: 0, types: new Set() };
        map[did].count += 1;
        if (n.type) map[did].types.add(n.type);
      }
      // Convertit Set en Array pour rendu plus simple
      const finalMap = {};
      Object.entries(map).forEach(([k, v]) => {
        finalMap[k] = { count: v.count, types: Array.from(v.types) };
      });
      setUnreadByDossier(finalMap);
    } catch (e) {
      // Silencieux : pas de notif, pas de badge, c'est tout
    }
  }, []);

  // Refresh notifs au mount + toutes les 30s
  useEffect(() => {
    loadUnreadNotifs();
    const interval = setInterval(loadUnreadNotifs, 30000);
    return () => clearInterval(interval);
  }, [loadUnreadNotifs]);

  // Marquer comme lues toutes les notifs d'un dossier (appelé à l'ouverture du drawer)
  const markDossierNotifsRead = useCallback(async (dossierId) => {
    if (!dossierId) return;
    try {
      const token = localStorage.getItem('lexback_token');
      await fetch(`${API_URL}/api/notifications/mark-read`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dossier_id: dossierId }),
      }).catch(() => {});
      // Optimiste : on retire localement
      setUnreadByDossier(prev => {
        const { [dossierId]: _, ...rest } = prev;
        return rest;
      });
    } catch {}
  }, []);

  // ── Charge les attachments en background pour afficher les indicateurs Statuts/PV/Kbis/RBE ──
  // Stratégie : on parallélise par lot de 5 pour ne pas saturer le serveur. Best effort.
  useEffect(() => {
    if (dossiers.length === 0) return;
    let cancelled = false;
    const role = user?.role || 'client';
    const BATCH_SIZE = 5;

    const fetchSummaryForDossier = async (dossier) => {
      try {
        const token = localStorage.getItem('lexback_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(`${API_URL}/api/documents/attachments/${dossier.id}`, { headers });
        if (!r.ok) return null;
        const json = await r.json();
        const buckets = groupAttachmentsByTab(json, role);
        return {
          dossier_id: dossier.id,
          Statuts: buckets.Statuts.length,
          PV: buckets.PV.length,
          Kbis: buckets.Kbis.length,
          RBE: buckets.RBE.length,
          Cession: buckets.Cession.length,
          Autres: buckets.Autres.length,
        };
      } catch { return null; }
    };

    const runBatches = async () => {
      // Ordre : commencer par les dossiers les plus récents (déjà triés desc par défaut)
      const queue = [...dossiers];
      while (queue.length > 0 && !cancelled) {
        const batch = queue.splice(0, BATCH_SIZE);
        const results = await Promise.all(batch.map(fetchSummaryForDossier));
        if (cancelled) return;
        setAttachmentsSummary(prev => {
          const next = { ...prev };
          results.forEach(r => { if (r) next[r.dossier_id] = r; });
          return next;
        });
      }
    };
    runBatches();
    return () => { cancelled = true; };
  }, [dossiers, user?.role]);

  const formalistesMap = useMemo(() => {
    const m = {};
    formalistes.forEach(f => { m[f.id] = f; });
    return m;
  }, [formalistes]);

  // Mise à jour locale d'un dossier après PATCH
  const handleDossierUpdate = useCallback((updated) => {
    setDossiers(list => list.map(d => d.id === updated.id ? updated : d));
    setSelectedDossier(updated);
  }, []);

  const filtered = useMemo(() => {
    let list = dossiers;
    // Filtre additionnel via query string ?status=...
    // (passé depuis le Dashboard quand on clique sur une pill "Actions requises")
    if (filterRawStatus) {
      list = list.filter(d => d.status === filterRawStatus);
    }
    // Filtre quick par relais
    if (filterStatus === 'lexback') list = list.filter(d => getDossierRelay(d) === 'lexback');
    else if (filterStatus === 'client') list = list.filter(d => getDossierRelay(d) === 'client');
    else if (filterStatus === 'greffe') list = list.filter(d => getDossierRelay(d) === 'greffe');
    else if (filterStatus === 'clos') list = list.filter(d => getDossierRelay(d) === 'clos');
    else if (filterStatus === 'annule') list = list.filter(d => getDossierRelay(d) === 'annule');
    // "Tous" = tous les dossiers sauf annulés (pour ne pas noyer la vue principale)
    else if (filterStatus === 'tous') list = list.filter(d => getDossierRelay(d) !== 'annule');
    // Filtre par labels statut multi
    if (filterStatusLabels.length > 0) {
      list = list.filter(d => filterStatusLabels.includes(d.status_label_id));
    }
    if (filterFrm) list = list.filter(d => d.formaliste_id === filterFrm);
    if (filterCabinet) list = list.filter(d => d.cabinet_id === filterCabinet || d.btob_cabinet === filterCabinet);
    if (filterFormalityType) list = list.filter(d => d.formality_type_id === filterFormalityType);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(d => (d.societe_nom||'').toLowerCase().includes(s) || (d.reference||'').toLowerCase().includes(s) || (d.societe_siren||'').includes(s));
    }

    // ── Filtres par colonne ──
    const cf = colFilters;
    if (cf.societe) {
      const s = cf.societe.toLowerCase();
      list = list.filter(d =>
        (d.societe_nom||'').toLowerCase().includes(s) ||
        (d.societe_siren||'').toLowerCase().includes(s)
      );
    }
    if (cf.type) list = list.filter(d => d.formality_type_id === cf.type);
    if (cf.subtypes) {
      list = list.filter(d => (d.sub_type_labels || []).some(s => s.id === cf.subtypes));
    }
    if (cf.statut) list = list.filter(d => d.status_label_id === cf.statut);
    if (cf.reference) {
      const s = cf.reference.toLowerCase();
      list = list.filter(d => (d.reference||'').toLowerCase().includes(s));
    }
    if (cf.dateFrom) {
      const from = new Date(cf.dateFrom + 'T00:00:00').getTime();
      list = list.filter(d => d.created_at && new Date(d.created_at).getTime() >= from);
    }
    if (cf.dateTo) {
      const to = new Date(cf.dateTo + 'T23:59:59').getTime();
      list = list.filter(d => d.created_at && new Date(d.created_at).getTime() <= to);
    }
    if (cf.formaliste) list = list.filter(d => d.formaliste_id === cf.formaliste);
    if (cf.greffe) list = list.filter(d => d.greffe_id === cf.greffe);
    if (cf.cabinet) list = list.filter(d => d.cabinet_id === cf.cabinet || d.btob_cabinet === cf.cabinet);
    if (cf.coutMin !== '') {
      const min = parseFloat(cf.coutMin);
      if (!isNaN(min)) list = list.filter(d => getDossierCosts(d).total >= min);
    }
    if (cf.coutMax !== '') {
      const max = parseFloat(cf.coutMax);
      if (!isNaN(max)) list = list.filter(d => getDossierCosts(d).total <= max);
    }

    // ── Tri ──
    const dir = sortDir === 'asc' ? 1 : -1;
    const getVal = (d) => {
      switch (sortKey) {
        case 'societe': return (d.societe_nom || '').toLowerCase();
        case 'type': return (d.formality_type_label?.name || d.type || '').toLowerCase();
        case 'statut': return (d.status_label?.name || d.status || '').toLowerCase();
        case 'reference': return (d.reference || '').toLowerCase();
        case 'date': return d.created_at ? new Date(d.created_at).getTime() : 0;
        case 'formaliste': return (d.formaliste_nom || d.formaliste_airtable || '').toLowerCase();
        case 'greffe': return (d.greffe_label?.name || d.greffe || '').toLowerCase();
        case 'cabinet': return (d.cabinet_label?.name || d.btob_cabinet || '').toLowerCase();
        case 'cout': return getDossierCosts(d).total;
        default: return 0;
      }
    };
    list = [...list].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });

    return list;
  }, [dossiers, filterStatus, filterRawStatus, filterStatusLabels, filterFrm, filterCabinet, filterFormalityType, search, colFilters, sortKey, sortDir]);

  const stats = {
    total: dossiers.length,
    en_cours: dossiers.filter(d => ['recu','assigne','en_cours'].includes(d.status)).length,
    termines: dossiers.filter(d => d.status === 'termine').length,
    rejetes: dossiers.filter(d => d.status === 'rejete').length,
  };

  // ── Compteurs par relais (calculés sur tous les dossiers, pas la liste filtrée) ──
  // "Tous" exclut les annulés pour ne pas polluer la vue principale.
  const relayCounts = useMemo(() => {
    const counts = { tous: 0, lexback: 0, client: 0, greffe: 0, clos: 0, annule: 0 };
    dossiers.forEach(d => {
      const r = getDossierRelay(d);
      counts[r] += 1;
      if (r !== 'annule') counts.tous += 1;
    });
    return counts;
  }, [dossiers]);

  const statusFilters = [
    { key:'tous',    label:'Tous',           count: relayCounts.tous    },
    { key:'lexback', label:'Chez Lexback',   count: relayCounts.lexback },
    { key:'client',  label:'Chez client',    count: relayCounts.client  },
    { key:'greffe',  label:'Chez le greffe', count: relayCounts.greffe  },
    { key:'clos',    label:'Clos',           count: relayCounts.clos    },
    { key:'annule',  label:'Annulés',        count: relayCounts.annule  },
  ];

  // ── Sélection multi ──
  const toggleSelect = (id) => {
    setSelectedIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  };
  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length) setSelectedIds([]);
    else setSelectedIds(filtered.map(d => d.id));
  };
  const clearSelection = () => setSelectedIds([]);

  const handleBulkUpdate = async (payload) => {
    const ids = [...selectedIds];
    let successCount = 0;
    let errorCount = 0;
    for (const id of ids) {
      try {
        await dossiersAPI.update(id, payload);
        successCount++;
      } catch (err) {
        errorCount++;
      }
    }
    alert(`${successCount} dossier(s) mis à jour${errorCount > 0 ? `, ${errorCount} erreur(s)` : ''}`);
    clearSelection();
    loadDossiers();
  };

  // ── Édition inline directement dans le tableau ──
  const handleInlineUpdate = async (dossierId, payload) => {
    try {
      const res = await dossiersAPI.update(dossierId, payload);
      setDossiers(list => list.map(d => d.id === dossierId ? res.data : d));
    } catch (err) {
      alert(formatApiError(err, 'Erreur lors de la mise à jour'));
    }
  };

  // ── Création inline d'un dossier (style Airtable, pas via wizard IA) ──
  // Utilise le flow brouillon pour rester compatible avec le backend :
  //   1. POST /api/dossiers/draft           → crée le brouillon
  //   2. POST /api/dossiers/draft/{id}/upload (xN si fichiers) → attache les pièces
  //   3. POST /api/dossiers/draft/{id}/submit → finalise le dossier
  // Le dossier final n'est pas renvoyé par submit, donc on relance loadDossiers().
  const handleInlineCreate = async () => {
    if (!inlineAdd) return;
    const societeNom = (inlineAdd.societe_nom || '').trim();
    const siren = (inlineAdd.societe_siren || '').replace(/\D/g, '').trim();
    const typeMain = inlineAdd.type_main || '';
    const files = Array.isArray(inlineAdd.files) ? inlineAdd.files : [];

    if (!typeMain) {
      alert('Le type de formalité est requis.');
      return;
    }
    if (typeMain !== 'creation' && !siren) {
      alert('Le SIREN est requis (sauf pour une création de société).');
      return;
    }
    if (typeMain === 'creation' && !societeNom) {
      alert('Le nom de la société est requis pour une création.');
      return;
    }
    if (siren && siren.length !== 9) {
      alert('Le SIREN doit comporter exactement 9 chiffres.');
      return;
    }

    setCreatingInline(true);
    try {
      // 1. Création du brouillon
      const draftPayload = typeMain === 'creation'
        ? { is_creation: true, type_formalite: typeMain }
        : { is_creation: false, siren, type_formalite: typeMain };
      const draftRes = await draftAPI.create(draftPayload);
      const draftIdNew = draftRes.data?.id;
      if (!draftIdNew) throw new Error('Brouillon non créé (ID manquant)');

      // 2. (Les fichiers sont uploadés APRÈS création, typés par sous-catégorie — voir étape 5.)
      const uploadErrors = [];

      // 3. Soumission directe (sans passer par l'IA)
      const submitPayload = {
        commentaire_final: societeNom && typeMain === 'creation' ? `Société à créer : ${societeNom}` : '',
        type_formalite: typeMain,
        sous_types: null,
        direct_submit: true,
        skip_missing_pieces: true,
        date_dossier: new Date().toISOString().slice(0, 10),
      };
      const submitRes = await draftAPI.submit(draftIdNew, submitPayload);

      // 4. Patch post-création : forcer le formality_type_id (catalogue) sur le dossier
      // pour que la pill "Formalité" s'affiche correctement et que la modale ait le
      // bon ID pré-sélectionné. Le backend ne sait pas toujours faire le mapping
      // type_formalite (string) → formality_type_id (UUID) automatiquement.
      const newDossierId = submitRes?.data?.id || submitRes?.data?.dossier_id;
      if (newDossierId) {
        // 4. Enrichissement : type formalité + sous-types + contacts client + notes
        const patch = {};
        if (societeNom) patch.societe_nom = societeNom;
        if (inlineAdd.formality_type_id) patch.formality_type_id = inlineAdd.formality_type_id;
        if ((inlineAdd.sub_type_ids || []).length) patch.sub_type_ids = inlineAdd.sub_type_ids;
        if (inlineAdd.formaliste_id) patch.formaliste_id = inlineAdd.formaliste_id;
        if ((inlineAdd.email_client_final || '').trim()) patch.email_client_final = inlineAdd.email_client_final.trim();
        if ((inlineAdd.telephone_client_final || '').trim()) patch.telephone_client_final = inlineAdd.telephone_client_final.trim();
        if ((inlineAdd.notes || '').trim()) patch.instruction_cabinet = inlineAdd.notes.trim();
        if (Object.keys(patch).length) {
          try { await dossiersAPI.update(newDossierId, patch); }
          catch (e) { console.warn('[InlineCreate] PATCH enrichissement échoué (non bloquant)', e); }
        }

        // 5. Upload des fichiers, typés par sous-catégorie (Statuts / PV / Kbis / …)
        if (files.length > 0) {
          const token = localStorage.getItem('lexback_token');
          await Promise.all(files.map(async (entry) => {
            try {
              const fd = new FormData();
              fd.append('file', entry.file);
              const url = `${API_URL}/api/documents/attachment/${newDossierId}?subcategory=${encodeURIComponent(entry.type || 'Autres')}`;
              const r = await fetch(url, { method:'POST', headers: token ? { Authorization:`Bearer ${token}` } : {}, body: fd });
              if (!r.ok) throw new Error(await r.text());
            } catch (e) {
              uploadErrors.push(`${entry.file?.name || 'fichier'} : ${formatApiError(e, 'upload échoué')}`);
            }
          }));
        }
      }

      // Recharge la liste pour récupérer le nouveau dossier
      await loadDossiers();
      setInlineAdd(null);

      if (uploadErrors.length > 0) {
        alert(`Dossier créé, mais ${uploadErrors.length} fichier(s) n'ont pas pu être uploadé(s) :\n• ${uploadErrors.join('\n• ')}`);
      }
    } catch (err) {
      alert(formatApiError(err, 'Erreur lors de la création du dossier'));
    } finally {
      setCreatingInline(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
        <div>
          <div className="section-title">{isClient ? 'Mes dossiers' : 'Dossiers'}</div>
          <div style={{ fontSize:'13px', color:'var(--color-text-secondary)', marginTop:'2px' }}>
            {isClient ? 'Suivez l\'avancement de vos formalités' : 'Gérez vos dossiers en cours'}
          </div>
        </div>
        {(canEdit || isClient) && (
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            <button
              onClick={() => setInlineAdd(inlineAdd ? null : { societe_nom:'', societe_siren:'', type_main:'', formality_type_id:'', files:[], sub_type_ids:[], formaliste_id:'', email_client_final:'', telephone_client_final:'', notes:'' })}
              title="Ajouter une ligne directement dans le tableau (sans passer par l'assistant)"
              style={{
                background: inlineAdd ? '#1A1916' : 'white',
                color: inlineAdd ? 'white' : '#1A1916',
                border: '0.5px solid rgba(0,0,0,0.15)',
                padding:'8px 14px', borderRadius:'8px', fontSize:'13px',
                cursor:'pointer', fontWeight:500,
                display:'inline-flex', alignItems:'center', gap:'6px',
              }}
            >
              <span style={{ fontSize:'14px', lineHeight:1 }}>+</span> Ajouter une ligne
            </button>
            <Link to="/dossiers/nouveau">
              <button style={{ background:'#B8860B', color:'white', border:'none', padding:'8px 18px', borderRadius:'8px', fontSize:'13px', cursor:'pointer', fontWeight:500 }}>
                + Nouveau dossier
              </button>
            </Link>
          </div>
        )}
      </div>

      {/* Bulk actions bar */}
      {canEdit && (
        <BulkActionsBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          catalog={catalog}
          onBulkUpdate={handleBulkUpdate}
        />
      )}

      {/* Bannière filtre actif via query (?status=doc_manquant ou rejete) */}
      {filterRawStatus && (
        <div style={{
          background: 'rgba(176,48,32,0.06)',
          border: '0.5px solid rgba(176,48,32,0.20)',
          borderRadius: '8px',
          padding: '8px 14px',
          marginBottom: '0.75rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '10px',
        }}>
          <div style={{ fontSize: '12px', color: '#B03020', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#B03020' }} />
            Filtre actif : <strong>{
              filterRawStatus === 'doc_manquant' ? 'Documents manquants' :
              filterRawStatus === 'rejete'       ? 'Rejets INPI à corriger' :
              filterRawStatus
            }</strong>
            <span style={{ color: 'rgba(176,48,32,0.6)', fontVariantNumeric: 'tabular-nums' }}>· {filtered.length} dossier{filtered.length > 1 ? 's' : ''}</span>
          </div>
          <button
            onClick={() => {
              setFilterRawStatus('');
              // Nettoie aussi l'URL pour ne pas réappliquer le filtre au prochain montage
              window.history.replaceState({}, '', '/dossiers');
            }}
            style={{
              background: 'transparent', border: '0.5px solid rgba(176,48,32,0.30)',
              color: '#B03020', padding: '4px 11px', borderRadius: '6px',
              fontSize: '11.5px', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >✕ Effacer</button>
        </div>
      )}

      {/* Filters bar — relais (Chez Lexback / Chez client / Chez le greffe / Clos) + recherche */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'1rem', flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ display:'flex', gap:'2px', padding:'3px', background:'#f3f3f0', borderRadius:'7px' }}>
          {statusFilters.map(f => (
            <button key={f.key} onClick={() => setFilterStatus(f.key)} style={{
              padding:'5px 11px', borderRadius:'5px', fontSize:'11.5px', cursor:'pointer',
              border:'none',
              background: filterStatus === f.key ? 'white' : 'transparent',
              color: filterStatus === f.key ? '#1d1d1f' : '#8b8576',
              fontWeight: filterStatus === f.key ? 500 : 400,
              boxShadow: filterStatus === f.key ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
              fontFamily: 'inherit',
              transition:'all 0.15s',
            }}>{f.label} · <span style={{ fontVariantNumeric:'tabular-nums' }}>{f.count}</span></button>
          ))}
        </div>
        <div style={{ flex:1 }} />
        <div style={{ position:'relative' }}>
          <span style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', fontSize:'12px', color:'var(--color-text-tertiary)', pointerEvents:'none' }}>⌕</span>
          <input
            placeholder="Rechercher société, SIREN, réf…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding:'6px 12px 6px 28px', borderRadius:'8px', border:'0.5px solid var(--color-border-secondary)', fontSize:'12.5px', background:'var(--color-background-primary)', color:'var(--color-text-primary)', width:'240px' }}
          />
        </div>
        {!isClient && formalistes.length > 0 && (
          <select value={filterFrm} onChange={e => setFilterFrm(e.target.value)} style={{ padding:'6px 10px', borderRadius:'8px', border:'0.5px solid var(--color-border-secondary)', fontSize:'12px', background:'var(--color-background-primary)', color:'var(--color-text-primary)', cursor:'pointer' }}>
            <option value="">Formaliste</option>
            {formalistes.map(f => <option key={f.id} value={f.id}>{f.prenom} {f.nom}</option>)}
          </select>
        )}
      </div>

      {/* Résultats */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px', fontSize:'11px', color:'var(--color-text-secondary)' }}>
        <div>
          {!loading && (
            <>{filtered.length} dossier{filtered.length > 1 ? 's' : ''} {filtered.length !== dossiers.length && <span style={{ color:'var(--color-text-tertiary)' }}>sur {dossiers.length}</span>}</>
          )}
        </div>
      </div>

      {/* Liste épurée — 1 ligne par dossier */}
      {loading || catalogLoading ? (
        <div style={{ padding:'2rem', textAlign:'center', color:'var(--color-text-secondary)' }}>Chargement...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding:'2rem', textAlign:'center', color:'var(--color-text-secondary)' }}>
          {search || filterFrm || filterStatus !== 'tous'
            ? <>Aucun dossier ne correspond. <button onClick={() => { setSearch(''); setFilterFrm(''); setFilterStatus('tous'); }} style={{ background:'transparent', border:'none', color:'#B8860B', cursor:'pointer', textDecoration:'underline' }}>Effacer les filtres</button></>
            : <>Aucun dossier.{' '}
              {canEdit && <Link to="/dossiers/nouveau" style={{ color:'#B8860B' }}>Soumettre votre premier dossier</Link>}</>
          }
        </div>
      ) : (
        <div style={{ background:'white', border:'0.5px solid rgba(0,0,0,0.08)', borderRadius:'12px', overflow:'hidden' }}>
          {/* Header colonnes */}
          <div style={{
            display:'grid',
            gridTemplateColumns: DOSSIER_GRID,
            padding:'9px 20px',
            background:'#fafaf8',
            gap:'12px',
            borderBottom:'0.5px solid rgba(0,0,0,0.04)',
            fontSize:'9.5px',
            color:'#8b8576',
            textTransform:'uppercase',
            letterSpacing:'0.06em',
            fontWeight:600,
            alignItems:'center',
          }}>
            <span>
              {canEdit && (
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && selectedIds.length === filtered.length}
                  ref={el => { if (el) el.indeterminate = selectedIds.length > 0 && selectedIds.length < filtered.length; }}
                  onChange={toggleSelectAll}
                  title="Tout sélectionner"
                  style={{ cursor:'pointer', accentColor:'#B8860B', width:'14px', height:'14px' }}
                />
              )}
            </span>
            <span>Date</span>
            <span>Société</span>
            <span>Statut</span>
            <span>Assigné</span>
            <span>Documents</span>
            <span style={{ textAlign:'right' }}>Actions</span>
          </div>

          {/* ── Ligne d'ajout inline (style Airtable) ── */}
          {(canEdit || isClient) && inlineAdd && (<>
            <div style={{
              display:'grid',
              gridTemplateColumns: DOSSIER_GRID,
              alignItems:'start',
              padding:'10px 20px',
              borderBottom:'0.5px solid rgba(0,0,0,0.06)',
              gap:'12px',
              background:'rgba(184,134,11,0.04)',
            }}>
              {/* Cellule case à cocher (vide en création) */}
              <span />
              {/* Date (auto = aujourd'hui) — déplacée à gauche */}
              <div style={{ fontSize:'11px', color:'#9aa3b0', fontStyle:'italic' }}>
                aujourd'hui
              </div>

              {/* Société + SIREN */}
              <div style={{ display:'flex', flexDirection:'column', gap:'4px', minWidth:0 }}>
                <input
                  autoFocus
                  value={inlineAdd.societe_nom}
                  onChange={e => setInlineAdd({ ...inlineAdd, societe_nom: e.target.value })}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleInlineCreate();
                    if (e.key === 'Escape') setInlineAdd(null);
                  }}
                  placeholder="Nom de la société…"
                  style={{
                    padding:'5px 8px', borderRadius:'5px',
                    border:'1px solid rgba(184,134,11,0.4)',
                    fontSize:'13px', background:'white',
                    fontWeight:500, color:'#1d1d1f',
                    outline:'none',
                  }}
                />
                <input
                  value={inlineAdd.societe_siren}
                  onChange={e => setInlineAdd({ ...inlineAdd, societe_siren: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleInlineCreate();
                    if (e.key === 'Escape') setInlineAdd(null);
                  }}
                  placeholder="SIREN (facultatif)"
                  style={{
                    padding:'4px 8px', borderRadius:'5px',
                    border:'0.5px solid rgba(0,0,0,0.12)',
                    fontSize:'11px', background:'white',
                    color:'#5C6B7A', fontVariantNumeric:'tabular-nums',
                    outline:'none',
                  }}
                />
                {/* Pappers : aperçu auto-rempli + boutons de téléchargement */}
                {(inlineAdd.societe_siren || '').replace(/\D/g, '').length === 9 && (
                  <div style={{ marginTop:'4px' }}>
                    {pappersInfo.loading && (
                      <div style={{ fontSize:'10.5px', color:'#9a8f7a', fontStyle:'italic' }}>
                        🔎 Récupération Pappers…
                      </div>
                    )}
                    {pappersInfo.error && !pappersInfo.loading && (
                      <div style={{ fontSize:'10.5px', color:'#B03020', fontStyle:'italic' }}>
                        {pappersInfo.error}
                      </div>
                    )}
                    {pappersInfo.societe && !pappersInfo.loading && (
                      <>
                        <div style={{ fontSize:'10.5px', color:'#1A7A4A', marginBottom:'4px' }}>
                          ✓ {pappersInfo.societe.nom}
                          {pappersInfo.societe.forme ? ` · ${pappersInfo.societe.forme}` : ''}
                        </div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
                          {[
                            { kind: 'statuts',    label: 'Statuts' },
                            { kind: 'extrait',    label: 'Extrait RNE' },
                            { kind: 'avis_insee', label: 'Avis INSEE' },
                          ].map(d => (
                            <button
                              key={d.kind}
                              onClick={() => downloadPappersDoc(pappersInfo.siren, d.kind, d.label)}
                              title={`Télécharger ${d.label}`}
                              style={{
                                background: 'rgba(91,141,239,0.08)',
                                border: '0.5px solid rgba(91,141,239,0.30)',
                                color: '#3b82f6',
                                padding: '2px 8px', borderRadius: '4px',
                                fontSize: '10.5px', cursor: 'pointer',
                                fontFamily: 'inherit',
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                              }}
                            >
                              📄 {d.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Statut (vide à la création — auto-rempli par le backend en "Reçu") */}
              <div style={{ fontSize:'11px', color:'#9aa3b0', fontStyle:'italic' }}>
                auto · Reçu
              </div>

              {/* Assigné (vide en création) */}
              <div />

              {/* Fichiers à uploader avec le dossier */}
              <div style={{ fontSize:'11px', color:'#8b8576' }}>
                {inlineAdd.files && inlineAdd.files.length > 0
                  ? `📎 ${inlineAdd.files.length} fichier(s)`
                  : <span style={{ fontStyle:'italic', color:'#aaa49a' }}>↓ détails ci-dessous</span>}
              </div>

              {/* Actions */}
              <div style={{ display:'flex', gap:'5px', justifyContent:'flex-end', alignSelf:'start' }}>
                <button
                  onClick={() => setInlineAdd(null)}
                  disabled={creatingInline}
                  style={{
                    background:'transparent', border:'0.5px solid rgba(0,0,0,0.12)',
                    color:'#5C6B7A', padding:'5px 11px', borderRadius:'5px',
                    fontSize:'11.5px', cursor: creatingInline ? 'not-allowed' : 'pointer',
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleInlineCreate}
                  disabled={creatingInline || !inlineAdd.type_main}
                  style={{
                    background: (!inlineAdd.type_main) ? 'rgba(184,134,11,0.5)' : '#B8860B',
                    color:'white', border:'none',
                    padding:'5px 13px', borderRadius:'5px',
                    fontSize:'11.5px', fontWeight:500,
                    cursor: creatingInline || !inlineAdd.type_main ? 'not-allowed' : 'pointer',
                  }}
                >
                  {creatingInline ? '…' : 'Créer'}
                </button>
              </div>
            </div>

            {/* ── Compléments du dossier (ajout rapide) ── */}
            <div style={{ padding:'18px 20px', background:'#f3efe6', borderTop:'1px solid rgba(184,134,11,0.18)', display:'flex', flexDirection:'column', gap:'12px' }}>
              <div style={{ fontSize:'12px', fontWeight:700, color:'#3d3527' }}>Compléments du dossier</div>

              {/* Carte 0 — Formalité (déplacée depuis l'ancienne colonne) */}
              <div style={_qaCard}>
                <div style={_qaHead}><span style={_qaDot} />Formalité</div>
                <select value={inlineAdd.formality_type_id}
                  onChange={e => {
                    const id = e.target.value;
                    const item = (catalog?.formality_types || []).find(t => t.id === id);
                    const name = (item?.name || '').toLowerCase();
                    let typeMain = 'modification';
                    if (name.includes('création') || name.includes('creation')) typeMain = 'creation';
                    else if (name.includes('modification') || name.includes('modif')) typeMain = 'modification';
                    else if (name.includes('dissolution') || name.includes('fermeture') || name.includes('clôtu') || name.includes('clotu') || name.includes('liquidation') || name.includes('radiation')) typeMain = 'fermeture';
                    else if (name.includes('dépôt') || name.includes('depot') || name.includes('acte') || name.includes('compte')) typeMain = 'depot_acte';
                    setInlineAdd({ ...inlineAdd, formality_type_id: id, type_main: typeMain });
                  }}
                  style={{ ..._qaField, cursor:'pointer', maxWidth:'320px' }}>
                  <option value="">— Choisir une formalité —</option>
                  {(catalog?.formality_types || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>

              {/* Carte 1 — Sous-types */}
              {(() => {
                const subs = (catalog?.formality_types || []).find(t => t.id === inlineAdd.formality_type_id)?.sub_types || [];
                const sel = inlineAdd.sub_type_ids || [];
                const toggle = (id) => setInlineAdd({ ...inlineAdd, sub_type_ids: sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id] });
                return (
                  <div style={_qaCard}>
                    <div style={_qaHead}><span style={_qaDot} />Sous-types {sel.length > 0 && <span style={_qaBadge}>{sel.length}</span>}</div>
                    {!inlineAdd.formality_type_id ? (
                      <div style={{ fontSize:'12px', color:'#9a8f7a', fontStyle:'italic' }}>Choisis d'abord une formalité pour voir ses sous-types.</div>
                    ) : subs.length === 0 ? (
                      <div style={{ fontSize:'12px', color:'#9a8f7a', fontStyle:'italic' }}>Aucun sous-type défini pour cette formalité — à configurer dans le catalogue.</div>
                    ) : (
                      <div style={{ display:'flex', flexWrap:'wrap', gap:'7px' }}>
                        {subs.map(st => {
                          const on = sel.includes(st.id);
                          return (
                            <button key={st.id} type="button" onClick={() => toggle(st.id)}
                              style={{ padding:'5px 12px', borderRadius:'999px', fontSize:'12px', fontWeight: on ? 600 : 500, cursor:'pointer',
                                border: on ? '1px solid #B8860B' : '1px solid #d8d1c0',
                                background: on ? '#B8860B' : '#fff', color: on ? '#fff' : '#4a4540', transition:'all .12s' }}>
                              {on ? '✓ ' : ''}{st.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Carte 2 — Assignation & contacts */}
              <div style={_qaCard}>
                <div style={_qaHead}><span style={_qaDot} />Assignation &amp; contacts</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px' }}>
                  <div>
                    <div style={_qaSubLbl}>Assigné à</div>
                    <select value={inlineAdd.formaliste_id || ''} onChange={e => setInlineAdd({ ...inlineAdd, formaliste_id: e.target.value || '' })} style={{ ..._qaField, cursor:'pointer' }}>
                      <option value="">— Non assigné —</option>
                      {(formalistes || []).map(f => <option key={f.id} value={f.id}>{f.prenom} {f.nom}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={_qaSubLbl}>Email client</div>
                    <input type="email" value={inlineAdd.email_client_final} onChange={e => setInlineAdd({ ...inlineAdd, email_client_final: e.target.value })} placeholder="client@exemple.fr" style={_qaField} />
                  </div>
                  <div>
                    <div style={_qaSubLbl}>Téléphone client</div>
                    <input value={inlineAdd.telephone_client_final} onChange={e => setInlineAdd({ ...inlineAdd, telephone_client_final: e.target.value })} placeholder="06 12 34 56 78" style={_qaField} />
                  </div>
                </div>
              </div>

              {/* Carte 3 — Notes */}
              <div style={_qaCard}>
                <div style={_qaHead}><span style={_qaDot} />Notes / instructions
                  <span style={{ fontSize:'10px', fontWeight:500, color:'#9a8f7a', textTransform:'none', letterSpacing:0 }}>· visible sur le dossier</span>
                </div>
                <textarea rows={2} value={inlineAdd.notes} onChange={e => setInlineAdd({ ...inlineAdd, notes: e.target.value })}
                  placeholder="Précisions pour le formaliste…" style={{ ..._qaField, resize:'vertical' }} />
              </div>

              {/* Carte 4 — Documents par type */}
              <div style={_qaCard}>
                <div style={_qaHead}><span style={_qaDot} />Documents par type {(inlineAdd.files||[]).length > 0 && <span style={_qaBadge}>{inlineAdd.files.length}</span>}</div>
                <label style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'6px 12px', borderRadius:'7px', border:'1px dashed #B8860B', background:'rgba(184,134,11,0.06)', fontSize:'12px', fontWeight:600, color:'#8a6d10', cursor:'pointer' }}>
                  📎 Ajouter des fichiers
                  <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }}
                    onChange={e => { const added = Array.from(e.target.files || []).map(file => ({ file, type:'Autres' })); setInlineAdd({ ...inlineAdd, files: [...(inlineAdd.files || []), ...added] }); e.target.value=''; }} />
                </label>
                {(inlineAdd.files || []).length > 0 && (
                  <div style={{ marginTop:'10px', display:'flex', flexDirection:'column', gap:'7px' }}>
                    {inlineAdd.files.map((entry, i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:'10px', background:'#faf8f3', border:'0.5px solid #ece7da', borderRadius:'7px', padding:'6px 10px' }}>
                        <span style={{ flex:1, fontSize:'12px', color:'#3d3527', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{entry.file.name}</span>
                        <select value={entry.type} onChange={e => { const t=e.target.value; setInlineAdd({ ...inlineAdd, files: inlineAdd.files.map((x, idx) => idx===i ? { ...x, type:t } : x) }); }}
                          style={{ fontSize:'12px', fontWeight:600, padding:'4px 8px', borderRadius:'6px', border:'1px solid #d8d1c0', background:'#fff', color:'#4a4030', cursor:'pointer' }}>
                          {['Statuts','PV','Kbis','RBE','Cession','Autres'].map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <button type="button" onClick={() => setInlineAdd({ ...inlineAdd, files: inlineAdd.files.filter((_, idx) => idx !== i) })}
                          style={{ background:'transparent', border:'none', color:'#9a8f7a', cursor:'pointer', fontSize:'16px', lineHeight:1 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Barre d'action — toujours visible (corrige le bouton Créer caché / petit écran) */}
              <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'center', gap:'10px', marginTop:'6px', paddingTop:'12px', borderTop:'1px solid rgba(184,134,11,0.18)' }}>
                {!inlineAdd.type_main && <span style={{ fontSize:'11.5px', color:'#9a8f7a', marginRight:'auto' }}>Choisis une formalité pour activer la création.</span>}
                <button type="button" onClick={() => setInlineAdd(null)} disabled={creatingInline}
                  style={{ background:'#fff', border:'0.5px solid rgba(0,0,0,0.18)', color:'#5C6B7A', padding:'10px 18px', borderRadius:'8px', fontSize:'13px', cursor: creatingInline ? 'not-allowed' : 'pointer' }}>Annuler</button>
                <button type="button" onClick={handleInlineCreate} disabled={creatingInline || !inlineAdd.type_main}
                  style={{ background: (creatingInline || !inlineAdd.type_main) ? '#d8c79a' : '#B8860B', color:'#fff', border:'none', padding:'11px 30px', borderRadius:'8px', fontSize:'14px', fontWeight:600, cursor: (creatingInline || !inlineAdd.type_main) ? 'not-allowed' : 'pointer', boxShadow: (creatingInline || !inlineAdd.type_main) ? 'none' : '0 2px 10px rgba(184,134,11,0.35)' }}>
                  {creatingInline ? 'Création…' : '+ Créer le dossier'}
                </button>
              </div>
            </div>
            </>
          )}

          {/* Lignes dossiers */}
          {filtered.map((d) => {
            const pillColor = getStatusPillColor(d);
            const statusLabel = d.status_label?.name || STATUS_LABELS[d.status] || d.status || '—';
            const summary = attachmentsSummary[d.id];
            const greffeName = d.greffe_label?.name || d.greffe;
            const dateLabel = (() => {
              if (!d.created_at) return '—';
              const dt = new Date(d.created_at);
              const days = Math.floor((Date.now() - dt.getTime()) / (1000 * 60 * 60 * 24));
              if (days === 0) return 'aujourd\'hui';
              if (days === 1) return 'hier';
              if (days < 30) return `il y a ${days}j`;
              if (days < 365) return `il y a ${Math.floor(days / 30)} mois`;
              return dt.toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric' });
            })();

            return (
              <div
                key={d.id}
                onClick={() => {
                  setSelectedDossier(d);
                  // Marque comme lues les notifs de ce dossier (si il y en a)
                  if (unreadByDossier[d.id]) markDossierNotifsRead(d.id);
                }}
                style={{
                  display:'grid',
                  gridTemplateColumns: DOSSIER_GRID,
                  alignItems:'center',
                  padding:'9px 20px',
                  borderBottom:'0.5px solid rgba(0,0,0,0.04)',
                  gap:'12px',
                  cursor:'pointer',
                  transition:'background 0.1s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#fafaf8'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                {/* Case à cocher (sélection multiple) */}
                <span onClick={e => e.stopPropagation()} style={{ display:'inline-flex' }}>
                  {canEdit && (
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(d.id)}
                      onChange={() => toggleSelect(d.id)}
                      style={{ cursor:'pointer', accentColor:'#B8860B', width:'14px', height:'14px' }}
                    />
                  )}
                </span>

                {/* Date relative + date+heure exactes (déplacée à gauche) */}
                <div style={{ display:'flex', flexDirection:'column', gap:'1px' }}>
                  <span style={{ fontSize:'11.5px', color:'#5C6B7A', fontVariantNumeric:'tabular-nums' }}>{dateLabel}</span>
                  {d.created_at && (
                    <span style={{ fontSize:'10px', color:'#9aa3b0', fontVariantNumeric:'tabular-nums' }}>
                      {new Date(d.created_at).toLocaleDateString('fr-FR', { day:'2-digit', month:'2-digit', year:'2-digit' })}
                      {' · '}
                      {new Date(d.created_at).toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' })}
                    </span>
                  )}
                </div>

                {/* Société + meta (sans le type, qui passe en colonne dédiée) */}
                <div style={{ minWidth:0, position:'relative' }}>
                  <div style={{ fontSize:'13.5px', fontWeight:500, color:'#1d1d1f', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', display:'flex', alignItems:'center', gap:'8px' }}>
                    {/* Badge notif non lue */}
                    {unreadByDossier[d.id] && unreadByDossier[d.id].count > 0 && (
                      <span
                        title={`${unreadByDossier[d.id].count} nouveauté${unreadByDossier[d.id].count > 1 ? 's' : ''} : ${unreadByDossier[d.id].types.join(', ')}`}
                        style={{
                          display:'inline-flex', alignItems:'center', justifyContent:'center',
                          minWidth:'18px', height:'18px',
                          padding:'0 5px',
                          borderRadius:'9px',
                          background:'#B8860B', color:'#fff',
                          fontSize:'10px', fontWeight:600,
                          fontVariantNumeric:'tabular-nums',
                          flexShrink:0,
                          boxShadow:'0 0 0 2px rgba(184,134,11,0.18)',
                        }}
                      >
                        {unreadByDossier[d.id].count > 9 ? '9+' : unreadByDossier[d.id].count}
                      </span>
                    )}
                    <span style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{d.societe_nom || '—'}</span>
                  </div>
                  <div style={{ fontSize:'10.5px', color:'#8b8576', marginTop:'2px', fontVariantNumeric:'tabular-nums', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {(() => {
                      const parts = [];
                      if (d.societe_siren) parts.push(d.societe_siren);
                      if (greffeName) parts.push(greffeName);
                      return parts.length > 0 ? parts.join(' · ') : '—';
                    })()}
                  </div>
                  {(() => {
                    const fm = d.formality_type_label?.name || (d.type ? d.type.replace(/_/g, ' ') : '');
                    const subs = (d.sub_type_labels || []).map(s => s.name).filter(Boolean);
                    if (!fm && subs.length === 0) return null;
                    return (
                      <div style={{ fontSize:'10px', color:'#a89e88', marginTop:'1px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}
                        title={[fm, ...subs].filter(Boolean).join(' · ')}>
                        {fm && <span style={{ color:'#8b8576' }}>{fm}</span>}
                        {subs.length > 0 && <span> · {subs.join(', ')}</span>}
                      </div>
                    );
                  })()}
                  {d.inpi_depot_id && (
                    <div style={{ fontSize:'10px', marginTop:'1px' }} onClick={e => e.stopPropagation()}>
                      <span style={{ color:'#8b8576' }}>Liasse · </span>
                      <CopyMini text={d.inpi_depot_id} />
                    </div>
                  )}
                </div>

                {/* Statut pill ovale */}
                <div>
                  <span style={{
                    display:'inline-flex',
                    alignItems:'center',
                    padding:'3px 10px',
                    borderRadius:'999px',
                    fontSize:'11px',
                    fontWeight:500,
                    background: pillColor.bg,
                    color: pillColor.fg,
                    textDecoration: pillColor.strikethrough ? 'line-through' : 'none',
                    whiteSpace:'nowrap',
                    maxWidth:'160px',
                    overflow:'hidden',
                    textOverflow:'ellipsis',
                  }} title={statusLabel}>
                    {statusLabel}
                  </span>
                </div>

                {/* Assigné — avatar éditable (menu collaborateurs) */}
                <div>
                  <AssigneCell
                    dossier={d}
                    formalistes={formalistes}
                    canEdit={canEdit}
                    onAssign={(id, fid) => handleInlineUpdate(id, { formaliste_id: fid })}
                  />
                </div>

                {/* Documents — pills cliquables (au clic : lightbox plein écran) */}
                <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }} onClick={e => e.stopPropagation()}>
                  {['Statuts', 'PV', 'Kbis', 'RBE', 'Cession', 'Autres'].map(cat => {
                    const has = summary && (summary[cat] || 0) > 0;
                    const loading = loadingPillFor === `${d.id}:${cat}`;
                    return (
                      <span
                        key={cat}
                        onClick={() => has && !loading && openDocsForCategory(d, cat)}
                        title={has ? `Voir les documents (${summary[cat]})` : 'Aucun document'}
                        style={{
                          display:'inline-flex',
                          alignItems:'center',
                          gap:'4px',
                          padding:'2px 8px',
                          borderRadius:'4px',
                          fontSize:'10.5px',
                          fontWeight:500,
                          background: has ? 'rgba(16,185,129,0.1)' : 'transparent',
                          color: has ? '#047857' : '#c7c7c5',
                          border: has ? '0.5px solid rgba(16,185,129,0.25)' : '0.5px dashed rgba(0,0,0,0.12)',
                          cursor: has ? 'pointer' : 'default',
                          opacity: loading ? 0.55 : 1,
                          transition: 'all 0.1s',
                        }}
                        onMouseEnter={e => { if (has && !loading) e.currentTarget.style.background = 'rgba(16,185,129,0.18)'; }}
                        onMouseLeave={e => { if (has && !loading) e.currentTarget.style.background = 'rgba(16,185,129,0.1)'; }}
                      >
                        {cat}
                        {has && <span style={{ fontSize:'9.5px', opacity:0.7, fontVariantNumeric:'tabular-nums' }}>· {summary[cat]}</span>}
                      </span>
                    );
                  })}
                </div>

                {/* Actions */}
                <div style={{ display:'flex', gap:'4px', justifyContent:'flex-end' }} onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => setChatDossier(d)}
                    title="Discussion"
                    style={{ width:'28px', height:'28px', borderRadius:'5px', border:'0.5px solid rgba(0,0,0,0.08)', background:'white', color:'#5C6B7A', display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:'13px', position:'relative', fontFamily:'inherit' }}
                  >💬</button>
                  <button
                    onClick={() => setSelectedDossier(d)}
                    title="Ajouter document"
                    style={{ width:'28px', height:'28px', borderRadius:'5px', border:'0.5px solid rgba(0,0,0,0.08)', background:'white', color:'#5C6B7A', display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:'13px', fontFamily:'inherit' }}
                  >📎</button>
                  <button
                    onClick={() => setSendModal({ dossier: d, mode: 'signature' })}
                    title="Envoyer pour signature"
                    style={{ width:'28px', height:'28px', borderRadius:'5px', border:'0.5px solid rgba(0,0,0,0.08)', background:'white', color:'#5C6B7A', display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:'13px', fontFamily:'inherit' }}
                  >✍️</button>
                  <button
                    onClick={() => setSendModal({ dossier: d, mode: 'client' })}
                    title="Envoyer au client"
                    style={{ width:'28px', height:'28px', borderRadius:'5px', border:'0.5px solid rgba(0,0,0,0.08)', background:'white', color:'#5C6B7A', display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:'13px', fontFamily:'inherit' }}
                  >📤</button>
                  <button
                    onClick={() => setSelectedDossier(d)}
                    title="Plus"
                    style={{ width:'28px', height:'28px', borderRadius:'5px', border:'0.5px solid rgba(0,0,0,0.08)', background:'white', color:'#5C6B7A', display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:'13px', fontFamily:'inherit' }}
                  >⋯</button>
                  {!['depose', 'termine'].includes(d.status) && (
                    <button
                      onClick={() => handleDeleteDossier(d)}
                      title="Supprimer le dossier"
                      style={{ width:'28px', height:'28px', borderRadius:'5px', border:'0.5px solid rgba(0,0,0,0.08)', background:'white', color:'#B03020', display:'inline-flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:'13px', fontFamily:'inherit' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#FCEBEB'; e.currentTarget.style.borderColor = '#E24B4A'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)'; }}
                    >🗑</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}


      {selectedDossier && (
        <DossierWorkspace
          dossier={selectedDossier}
          onClose={() => setSelectedDossier(null)}
          onUpdate={handleDossierUpdate}
          canEdit={canEdit}
          currentUserId={user?.id}
          currentUserRole={user?.role}
          clientCanModify={isClient && selectedDossier && !isDossierLocked(selectedDossier)}
        />
      )}

      <QuickChatDrawer
        dossier={chatDossier}
        onClose={() => setChatDossier(null)}
        currentUserId={user?.id}
        currentUserRole={user?.role}
        canEdit={canEdit}
      />

      {sendModal && (
        <SendDocumentsModal
          dossier={sendModal.dossier}
          mode={sendModal.mode}
          onClose={() => setSendModal(null)}
          onSent={() => { setSendModal(null); handleDossierUpdate && handleDossierUpdate(); }}
        />
      )}

      {/* Lightbox plein écran (depuis les pills de docs dans la table) */}
      {tableLightbox && (
        <DocLightbox
          docs={tableLightbox.docs}
          initial={tableLightbox.initial}
          onClose={() => setTableLightbox(null)}
        />
      )}

      <style>{`
        @media (max-width: 768px) {
          .hide-mobile { display: none !important; }
        }
      `}</style>
    </div>
  );
}

const thStyle = {
  textAlign:'left', padding:'8px 6px', fontSize:'10px', fontWeight:600,
  textTransform:'uppercase', letterSpacing:'0.04em',
  color:'var(--color-text-tertiary)', borderBottom:'1px solid var(--color-border-secondary)',
};
const tdStyle = { padding:'10px 10px', verticalAlign:'middle' };

const thStyleSticky = {
  textAlign:'left', padding:'12px 10px', fontSize:'10.5px', fontWeight:700,
  textTransform:'uppercase', letterSpacing:'0.05em',
  color:'var(--color-text-primary)',
  borderBottom:'2px solid #B8860B',
  background:'#f5f1ea',
  position:'sticky', top:0, zIndex:2,
  whiteSpace:'nowrap',
};

const thFilterStyle = {
  padding:'6px 10px 8px',
  borderBottom:'1px solid var(--color-border-secondary)',
  background:'#faf7f1',
  position:'sticky', top:'42px', zIndex:1,
  verticalAlign:'top',
};

const inputColFilter = {
  width:'100%',
  padding:'3px 6px',
  borderRadius:'4px',
  border:'0.5px solid var(--color-border-secondary)',
  fontSize:'11px',
  background:'var(--color-background-primary)',
  color:'var(--color-text-primary)',
  boxSizing:'border-box',
};

const selectColFilter = {
  ...inputColFilter,
  cursor:'pointer',
};

function SortableTh({ label, sortKey, currentKey, dir, onClick, hideMobile, align }) {
  const isActive = currentKey === sortKey;
  return (
    <th
      onClick={() => onClick(sortKey)}
      className={hideMobile ? 'hide-mobile' : ''}
      style={{
        ...thStyleSticky,
        textAlign: align || 'left',
        cursor:'pointer',
        userSelect:'none',
        color: isActive ? '#B8860B' : 'var(--color-text-primary)',
      }}
    >
      <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', width: align === 'right' ? '100%' : 'auto' }}>
        {label}
        <span style={{ fontSize:'9px', opacity: isActive ? 1 : 0.35, lineHeight:1 }}>
          {isActive ? (dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </span>
    </th>
  );
}
